import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

// The material family (shared by the clog body and strap GLBs) that gets tinted live. Matched
// by prefix, not exact equality — the exact suffix has already drifted between two separate
// exports from the 3D team (seen as both "Crocs_Duet_Sport_Clog.001" and
// "Crocs_Duet_Sport_Clog" with no suffix), and an exact-string match would silently stop
// tinting anything if that happens again with no visible error.
const TINTABLE_MATERIAL_PREFIX = 'Crocs_Duet_Sport_Clog';

// Charm attachment points are read live from named nodes in the clog GLB itself (e.g.
// "charm 1", "charm 2"...) — see findCharmAnchors() below. This hardcoded list is only a
// fallback for a model that doesn't have those marker nodes at all.
const FALLBACK_CHARM_SLOT_POSITIONS = [
  [-0.030, 0.100, 0.055],
  [0.000, 0.108, 0.065],
  [0.030, 0.100, 0.055],
  [-0.030, 0.095, 0.015],
  [0.000, 0.103, 0.020],
  [0.030, 0.095, 0.015],
];
const CHARM_SLOT_SIZE = 0.05; // plane diameter in metres — sized to read clearly at a glance;
// the actual visible shape is whatever the charm's real (background-removed) silhouette is,
// which may or may not fill this whole disc
// Fallback only: used when the real surface normal can't be computed (see
// computeOutwardNormal below) — e.g. a marker that isn't actually touching the mesh surface.
const CHARM_FACING_FALLBACK_TARGET = new THREE.Vector3(0, 0.05, -0.03);

function findCharmAnchors(root) {
  // Force matrices up to date before reading world positions — needed because some exports
  // (this one included) parent everything under a node that itself carries a real rotation
  // (a common exporter trick to convert a Z-up-authored mesh to glTF's required Y-up at
  // render time). A marker's *local* position only means what it looks like once that
  // ancestor rotation is accounted for, so world position is the only frame that's safe to
  // use once we re-parent these onto our own standalone billboard meshes.
  root.updateMatrixWorld(true);
  const found = [];
  root.traverse((obj) => {
    // GLTFLoader sanitizes node names — a "charm 1" node (space, as authored in Blender)
    // arrives here as "charm_1" (underscore). Accept either so a future export using spaces,
    // underscores, or no separator at all still matches.
    const m = /^charm[\s_]*(\d+)$/i.exec((obj.name || '').trim());
    if (m) found.push({ index: Number(m[1]), position: obj.getWorldPosition(new THREE.Vector3()) });
  });
  found.sort((a, b) => a.index - b.index);
  return found.map((f) => f.position);
}

// The marker nodes carry no rotation data, so instead of aiming every charm at one fixed
// guessed point (which looks fine for some slots and puts others nearly edge-on to the
// camera), fire a ray from an interior reference point, through the marker, and out through
// the actual shoe mesh — the surface normal at that exit point is a real, per-slot "outward"
// direction. Falls back to the fixed target only if the ray doesn't hit anything (e.g. a
// marker that isn't actually sitting on the surface).
// The delivered model has small (~26-vertex) indicator-sphere meshes sitting at each charm
// marker position — siblings of the named "charm N" empties, not children of them, so they
// can't be excluded by name/hierarchy. Excluding by vertex count instead: the real clog body
// meshes have thousands of vertices, so a low threshold cleanly separates "real shoe surface"
// from "tiny marker indicator" regardless of how a future export names or nests it.
const MIN_SURFACE_VERTEX_COUNT = 200;
function collectSurfaceMeshes(meshRoot) {
  const meshes = [];
  meshRoot.traverse((o) => {
    if (!o.isMesh) return;
    const vertCount = o.geometry?.attributes?.position?.count || 0;
    if (vertCount >= MIN_SURFACE_VERTEX_COUNT) meshes.push(o);
  });
  return meshes;
}

// Finds the point on the mesh surface closest to `point` by brute-force checking every
// triangle, returning that point plus the triangle's (world-space) face normal. Run once per
// marker at setup time, not per frame, so the O(triangle count) cost is negligible.
function findNearestSurfacePoint(point, meshes) {
  const triangle = new THREE.Triangle();
  const vA = new THREE.Vector3(), vB = new THREE.Vector3(), vC = new THREE.Vector3();
  const closest = new THREE.Vector3();
  let best = null;
  meshes.forEach((mesh) => {
    const geom = mesh.geometry;
    const posAttr = geom.attributes.position;
    const index = geom.index;
    const triCount = index ? index.count / 3 : posAttr.count / 3;
    for (let t = 0; t < triCount; t++) {
      const ia = index ? index.getX(t * 3) : t * 3;
      const ib = index ? index.getX(t * 3 + 1) : t * 3 + 1;
      const ic = index ? index.getX(t * 3 + 2) : t * 3 + 2;
      vA.fromBufferAttribute(posAttr, ia).applyMatrix4(mesh.matrixWorld);
      vB.fromBufferAttribute(posAttr, ib).applyMatrix4(mesh.matrixWorld);
      vC.fromBufferAttribute(posAttr, ic).applyMatrix4(mesh.matrixWorld);
      triangle.set(vA, vB, vC);
      triangle.closestPointToPoint(point, closest);
      const distSq = closest.distanceToSquared(point);
      if (!best || distSq < best.distSq) {
        const normal = new THREE.Vector3();
        triangle.getNormal(normal); // vA/vB/vC are already world-space, so this normal is too
        best = { distSq, point: closest.clone(), normal };
      }
    }
  });
  return best;
}

// The previous approach fired a single ray from a *guessed* interior reference point through
// the marker and read the normal off whatever it hit first. That guess (straight in from the
// shoe's vertical centerline) is wrong for a marker near the toe centerline or the front curve
// — the ray can graze past the intended local surface and hit the *far* interior wall instead,
// coming back with a normal that faces back into the shoe rather than outward (the reported
// "charm mostly inside the clog, cropped" bug). Raycasting-through-solid-geometry is inherently
// fragile that way: which surface it happens to hit first depends on the whole mesh, not just
// the local area around the marker. Finding the closest surface point directly (no raycast, no
// guessed reference) is immune to that failure mode — a marker authored on or near the shoe
// skin always resolves to the true local normal at that skin, regardless of the shoe's overall
// shape or which side of an opening the marker sits near.
// Average position of every vertex across the shoe's surface meshes — a stand-in for "a point
// safely inside the solid shoe body", used only to disambiguate which way a surface normal
// should point (see computeOutwardNormal). Computed once per model load, not per marker.
function computeMeshCentroid(meshRoot) {
  const centroid = new THREE.Vector3();
  const v = new THREE.Vector3();
  let count = 0;
  collectSurfaceMeshes(meshRoot).forEach((mesh) => {
    const posAttr = mesh.geometry.attributes.position;
    for (let i = 0; i < posAttr.count; i++) {
      v.fromBufferAttribute(posAttr, i).applyMatrix4(mesh.matrixWorld);
      centroid.add(v);
      count++;
    }
  });
  return count ? centroid.divideScalar(count) : centroid;
}

// Averaging nearby triangles here (instead of just using the single nearest one) was tried —
// motivated by real evidence that the single-nearest read is noisy on this mesh — but it traded
// one problem for a worse one: the averaged direction, while more internally consistent between
// markers, pointed far enough from the single-triangle reading that at the actual default
// product-camera angle, checked directly, charms went from "visible, if imperfectly placed" to
// "edge-on and effectively invisible." Whatever this mesh's geometric normal technically is at
// these points, the single nearest triangle is the reading that has actually kept charms visible
// and correctly facing across every real check against the default camera — that's the
// higher-priority property here, even on the rounds where it left something else imperfect.
function computeOutwardNormal(markerPos, meshRoot, centroid) {
  const nearest = findNearestSurfacePoint(markerPos, collectSurfaceMeshes(meshRoot));
  if (!nearest) return null;
  const resolved = nearest.normal;
  // The closest surface point can legitimately belong to either side of a thin double-walled
  // shell (this model's charm markers sit right where the outer skin and the inner wall of a
  // perforation rim are only a couple of millimetres apart) — geometric distance alone can't
  // tell outer from inner, and a raw face normal is just as likely to come back pointing into
  // the shoe as out of it. Resolve the sign with a direction hint instead of trusting the
  // triangle's winding: the vector from the whole shoe's vertex centroid (reliably inside the
  // solid body) out to the marker approximates the true outward radial direction on every axis.
  const outwardHint = markerPos.clone().sub(centroid);
  if (outwardHint.lengthSq() > 1e-8 && resolved.dot(outwardHint) < 0) resolved.negate();
  return resolved;
}

// The marker's authored position sometimes sits a little off the true centre of the nearest
// perforation hole (an authoring precision issue, not something worth round-tripping to the 3D
// team over) — leaving a sliver of the actual hole visible next to the charm instead of fully
// covered, the way a real clip-in charm covers the hole it plugs into. This finds the hole's
// real centre directly from the geometry: sample a grid of points in the plane tangent to the
// surface normal, and for each, fire a ray back toward the surface along -normal — a ray that
// hits nothing found a gap in the mesh (the hole); one that hits found solid material.
// Returns { position, normal } — normal is the surface direction re-measured right at that
// position (see below for why), or null if that couldn't be measured reliably. `centroid` is
// the same whole-mesh vertex centroid computeOutwardNormal uses, needed for the same reason:
// disambiguating which side of a thin double wall a raw triangle normal belongs to.
function findNearestHoleCenter(markerPos, normal, meshRoot, centroid) {
  const up = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  const tangentA = new THREE.Vector3().crossVectors(up, normal).normalize();
  const tangentB = new THREE.Vector3().crossVectors(normal, tangentA).normalize();
  const meshes = collectSurfaceMeshes(meshRoot);

  // This model's perforations turn out to be closely-spaced, irregularly-shaped slots, not
  // small isolated round holes with generous clearance around them — measured directly by
  // scanning the geometry, several markers have another opening (or a large vent, wider than
  // a single perforation) within just 10-15mm. A search window wide enough to comfortably find
  // a hole the marker might be off-centre from is therefore *also* wide enough to wander into
  // that neighbour, or a nearby vent that isn't a snap target at all — both measured causes of
  // reported bugs (a charm centred between two holes; a charm dragged toward a large vent
  // instead of the small perforation next to it). Kept deliberately small — this can only ever
  // nudge the marker a few millimetres, not relocate it to a different opening entirely.
  const SEARCH_RADIUS = 0.008; // metres — wide enough to contain a whole nearby perforation
  const STEPS = 8; // grid half-width in samples; spacing = SEARCH_RADIUS / STEPS = 1mm
  const RAY_START_OFFSET = 0.01; // start well outside the mesh, ray back in along -normal

  // Sample a grid of points in the plane tangent to the surface normal; a ray fired back
  // toward the surface along -normal that hits nothing found a gap (a hole), one that hits
  // found solid material.
  const size = STEPS * 2 + 1;
  const mid = STEPS;
  const offMesh = new Array(size * size).fill(false);
  for (let ix = -STEPS; ix <= STEPS; ix++) {
    for (let iy = -STEPS; iy <= STEPS; iy++) {
      const u = (ix / STEPS) * SEARCH_RADIUS;
      const v = (iy / STEPS) * SEARCH_RADIUS;
      if (u * u + v * v > SEARCH_RADIUS * SEARCH_RADIUS) continue;
      const samplePos = markerPos.clone()
        .addScaledVector(tangentA, u)
        .addScaledVector(tangentB, v)
        .addScaledVector(normal, RAY_START_OFFSET);
      const raycaster = new THREE.Raycaster(samplePos, normal.clone().negate(), 0, RAY_START_OFFSET * 3);
      if (!raycaster.intersectObjects(meshes, false).length) offMesh[(ix + mid) * size + (iy + mid)] = true;
    }
  }

  // Averaging every "off-mesh" sample's position gives the centre of *whatever shape the void
  // happens to be* — for a clean isolated circular hole that's correct, but this model's
  // perforations sit close enough together that the void within one search window can be an
  // elongated or dumbbell-shaped region spanning parts of two adjacent holes joined by a
  // narrow gap, and the average of that shape lands in open space between them, on neither
  // hole (confirmed directly — placing a marker at that average and rendering it showed it
  // sitting in the gap between two real holes).
  // Picking the single *deepest* off-mesh cell (farthest from any solid edge) was the fix for
  // that — immune to the two-holes-joined shape without needing to separate the void into
  // regions first. But "deepest" broke down at one marker sitting on a sharply curved part of
  // the toe (confirmed directly with debug markers rendered on the real mesh): this whole
  // tangent-plane search assumes the surface is locally flat around the marker, and on a patch
  // that curves fast, a point several millimetres away that's genuinely nowhere near the real
  // hole can still read as "off-mesh" (the flat sample plane has drifted off the true curved
  // surface out there, not because there's an actual opening) and outscore the real hole on raw
  // depth. The marker's *own* authored position sits right at that real hole's edge in every
  // case checked — the artist placed it there — so once a cell clears the same depth bar (still
  // required, to reject a sliver 2mm wide or less as seam/sampling noise), prefer whichever one
  // is physically closest to the marker over whichever reads deepest. A real hole close to the
  // marker will still win against a same-depth false reading further out; it just stops a
  // curvature-corrupted distant reading from ever being able to outrank the real, nearby hole.
  // Grid is small (a few hundred cells) so brute force here is trivial — no need for anything
  // cleverer than "check every pair."
  let best = null, bestDepth = -1, bestDistFromCenter = Infinity;
  for (let ix = 0; ix < size; ix++) {
    for (let iy = 0; iy < size; iy++) {
      if (!offMesh[ix * size + iy]) continue;
      // Distance (in grid steps) to the nearest solid cell or the edge of the sampled window —
      // treating the window edge as "unknown, assume solid" keeps a void that runs off the edge
      // of the search radius (a large vent, not a small hole) from reading as arbitrarily deep
      // just because we stopped sampling before reaching its actual boundary.
      let depth = Math.min(ix, size - 1 - ix, iy, size - 1 - iy);
      for (let jx = 0; jx < size; jx++) {
        for (let jy = 0; jy < size; jy++) {
          if (offMesh[jx * size + jy]) continue;
          const d = Math.max(Math.abs(jx - ix), Math.abs(jy - iy));
          if (d < depth) depth = d;
        }
      }
      // A depth of 0-1 grid steps (a sliver 2mm wide or less) isn't a real perforation a charm
      // is meant to plug into — likely a seam or sampling noise.
      if (depth < 2) continue;
      const distFromCenter = Math.hypot(ix - mid, iy - mid);
      if (distFromCenter < bestDistFromCenter) { bestDistFromCenter = distFromCenter; bestDepth = depth; best = [ix, iy]; }
    }
  }
  if (!best) return null;

  // The deepest single grid cell is only as precise as the 1mm grid spacing itself, and can sit
  // a little off the hole's true (continuous) centre if the sampled shape isn't perfectly
  // symmetric — visible as a hole still peeking out on one side even though the charm is
  // genuinely attached to the right hole. Averaging every off-mesh cell within a small, tight
  // radius of that validated deepest point recovers sub-grid precision the same way the old
  // whole-window average did for a clean isolated hole — but a *second* hole close enough to
  // bridge into the search window is (by construction, since this cell was picked for being the
  // single deepest point) farther from here than from the window's original centre, so this
  // tight local average doesn't reintroduce the two-holes-averaged-together bug this was built
  // to avoid.
  const REFINE_RADIUS = 2; // grid steps (~2mm) — well under typical hole spacing on this model
  let cu = 0, cv = 0, count = 0;
  for (let ix = Math.max(0, best[0] - REFINE_RADIUS); ix <= Math.min(size - 1, best[0] + REFINE_RADIUS); ix++) {
    for (let iy = Math.max(0, best[1] - REFINE_RADIUS); iy <= Math.min(size - 1, best[1] + REFINE_RADIUS); iy++) {
      if (!offMesh[ix * size + iy]) continue;
      if (Math.max(Math.abs(ix - best[0]), Math.abs(iy - best[1])) > REFINE_RADIUS) continue;
      cu += (ix - mid) / STEPS * SEARCH_RADIUS;
      cv += (iy - mid) / STEPS * SEARCH_RADIUS;
      count++;
    }
  }
  const u = count ? cu / count : ((best[0] - mid) / STEPS) * SEARCH_RADIUS;
  const v = count ? cv / count : ((best[1] - mid) / STEPS) * SEARCH_RADIUS;
  const finalPos = markerPos.clone().addScaledVector(tangentA, u).addScaledVector(tangentB, v);

  // Orientation still shouldn't be read at a point inside/near the hole itself — that's the
  // unstable bevel-rim read that broke a marker before. But using the *original* marker's
  // normal for everything doesn't work either: on a sharply curved area, a several-millimetre
  // snap can land on a patch of skin that genuinely faces a different way, and orienting the
  // plane with the stale, pre-snap normal makes it look tilted / half-sunk relative to the
  // surface it's actually resting on (confirmed — that's what was reported here). The fix is
  // sampling points just past the hole's edge, deliberately outside the void — genuinely on
  // the flat skin immediately surrounding this specific hole, not the hole's own interior.
  //
  // A fixed-radius ring around the centre was the first attempt at this, and it measurably
  // failed: these perforations are elongated/irregular, not round, so a circle sized to clear
  // the *short* axis is still deep inside the void along the *long* axis — most ring samples
  // landed back in the hole instead of on skin, and the handful that did hit skin ended up
  // being an arbitrary, unrepresentative corner rather than the surrounding surface. Marching
  // outward from the centre *in each sample direction independently*, stopping as soon as that
  // specific direction first reaches solid material, adapts to however far the void actually
  // extends that way — reliably landing just past the true edge in every direction regardless
  // of the hole's shape.
  const MARCH_STEP = SEARCH_RADIUS / STEPS; // 1mm — matches the grid's own resolution
  const MARCH_MAX = SEARCH_RADIUS * 2.5; // give up past this; a hole this large isn't a small perforation
  const orientNormal = new THREE.Vector3();
  let orientCount = 0;
  const DIRECTIONS = 8;
  for (let k = 0; k < DIRECTIONS; k++) {
    const angle = (k / DIRECTIONS) * Math.PI * 2;
    const dirA = Math.cos(angle), dirB = Math.sin(angle);
    for (let dist = MARCH_STEP; dist <= MARCH_MAX; dist += MARCH_STEP) {
      const point = finalPos.clone().addScaledVector(tangentA, dirA * dist).addScaledVector(tangentB, dirB * dist);
      const checkPos = point.clone().addScaledVector(normal, RAY_START_OFFSET);
      const raycaster = new THREE.Raycaster(checkPos, normal.clone().negate(), 0, RAY_START_OFFSET * 3);
      if (!raycaster.intersectObjects(meshes, false).length) continue; // still void this far out — keep marching
      const nearest = findNearestSurfacePoint(point, meshes);
      if (nearest) {
        // Same ambiguity as computeOutwardNormal, and the same fix: a raw triangle normal here
        // can belong to either side of a thin double wall, so resolve the sign against the
        // whole-mesh centroid before folding it into the average.
        const sampleNormal = nearest.normal;
        const outwardHint = point.clone().sub(centroid);
        if (outwardHint.lengthSq() > 1e-8 && sampleNormal.dot(outwardHint) < 0) sampleNormal.negate();
        orientNormal.add(sampleNormal);
        orientCount++;
      }
      break; // found this direction's edge (or a usable normal failed) — move to the next direction
    }
  }

  return { position: finalPos, normal: orientCount ? orientNormal.normalize() : null };
}

// Where the preview camera sits — the single source of truth for it (initClogBuilder sets the
// actual camera position by copying this, rather than repeating the numbers), since
// orientToNormal also needs this same point to know which way to face every charm. Rotation is
// locked (see controls.enableRotate = false below), so this is the only angle that matters.
// Z is negative here on purpose — measured directly off the model's own bounding box, the toe
// (where every charm marker actually sits) is the negative-Z end and the heel is positive-Z. A
// positive-Z camera position was found to be sitting behind the heel looking toward the toe —
// showing the closed heel/back of the shoe as the "front", with every charm facing away from
// the camera (and therefore culled to invisible, since they're FrontSide) — confirmed directly
// by temporarily re-orienting charms toward a positive-Z point and watching them disappear.
// Moved farther out and higher above the shoe (a steeper look-down angle, showing more of the
// top surface the charms actually sit on) than the original, more zoomed-out/flatter default,
// paired with a narrower field of view (see the PerspectiveCamera call below) — the shoe used to
// sit small in the preview frame with a lot of empty margin on every side. Two wrong approaches
// were tried before landing here: a lower FOV alone just magnifies around the fixed look-at point
// at the same close distance and crops the heel/toe tips out of frame; moving the camera *closer*
// along the original angle instead of farther fills the frame too, but a wide lens that close to a
// small object visibly distorts its proportions (a mild fisheye/dolly-zoom effect) — confirmed
// directly, the shoe's apparent angle looked wrong even though the true camera bearing hadn't
// changed. Moving farther away and narrowing the FOV to compensate (the same principle a telephoto
// product-photography lens uses to avoid wide-angle distortion) fills the frame just as much
// without that distortion.
const DEFAULT_VIEW_POINT = new THREE.Vector3(0, 0.4295, -0.694);

// Every charm marker's real local surface normal points a genuinely different direction —
// measured directly, from ~25° to ~70°+ off the camera depending on the marker, because the
// shoe's surface curves substantially over the few centimetres between them. Orienting each
// charm to its own local normal (the natural-seeming approach, and the one this went through
// many rounds trying to make work) means each one reads at a different, sometimes very oblique
// angle — flush with the true surface, but some near edge-on and barely legible from the one
// angle a customer actually sees this preview from, now that it no longer rotates (see
// controls.enableRotate above). Since that angle is now fixed and known, every charm can
// instead simply be oriented to face it directly, like a label facing the camera rather than
// lying flush with the surface underneath it — trading a small amount of physical realism
// (checked directly against the earlier surface-flush version: the difference reads as the
// charm standing very slightly proud of the surface, not as visibly wrong) for every marker
// being clearly readable, which is the property that actually matters here.
function orientToNormal(mesh) {
  const toCamera = DEFAULT_VIEW_POINT.clone().sub(mesh.position).normalize();
  // A marker positioned almost exactly below the camera can't use world-up as its up-reference
  // — the cross product degenerates near zero. Fall back to a horizontal reference axis for
  // just those slots so the basis stays well-defined.
  const upHint = Math.abs(toCamera.y) > 0.95 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
  const xAxis = new THREE.Vector3().crossVectors(upHint, toCamera).normalize();
  const yAxis = new THREE.Vector3().crossVectors(toCamera, xAxis).normalize();
  const basis = new THREE.Matrix4().makeBasis(xAxis, yAxis, toCamera);
  mesh.quaternion.setFromRotationMatrix(basis);
  // No extra flip here — CircleGeometry's front face (the one a FrontSide material renders)
  // sits on local +Z, the same axis this basis points straight at the camera, confirmed by
  // direct A/B test (a plain circle built with exactly this basis and no correction rendered
  // correctly under FrontSide; adding a 180° flip on top of it made it vanish). An earlier
  // version of this function added a flip here based on a similar-looking test against the
  // *previous* (surface-normal-based) basis, which wasn't a safe result to carry over — it
  // turned out to depend on which specific direction that basis's Z axis happened to be built
  // from, not on a fixed property of the geometry itself.
}

// Step definitions. "kind: single" is the existing one-colour-at-a-time swatch UI; "kind:
// multi" is the charm grid (pick several, up to maxCharms). configKey points at the matching
// array in the JSON data island. Only steps whose array is non-empty get a tab, so wiring up
// a future "sockees" step is just a matter of adding data — no layout changes needed.
// "kind: size" is the mandatory first step (see buildSizeList below) — its `items` are the
// distinct EU sizes found across every clog colour's real variants, not a configKey read
// straight off `config`, so it's built specially in the steps map rather than through the
// generic `config[def.configKey]` path the other kinds use.
const STEP_DEFS = [
  { key: 'size', kind: 'size', configKey: null, tabLabel: 'Size', stepText: 'Pick your size' },
  { key: 'clog', kind: 'single', configKey: 'clogColors', tabLabel: 'Clog', stepText: 'Choose your clog colour' },
  { key: 'strap', kind: 'single', configKey: 'strapColors', tabLabel: 'Strap', stepText: 'Choose your strap colour' },
  { key: 'charm', kind: 'multi', configKey: 'charms', tabLabel: 'Charms', stepText: 'Choose your charms' },
];

// Real-world EU↔Pakistani shoe-size conversion for this store's clog line turned out NOT to be a
// constant offset — confirmed directly against the merchant's own chart, the offset is 33 for the
// lower ranges (37-38, 39-40) but shifts to 34 for 41-42/43-44, and 45-46 isn't a whole-number
// step at all. Kept as an explicit lookup table rather than a formula since the real chart is
// non-linear; EU_TO_PK_OFFSET below is only a fallback formula for a range this table doesn't
// list (e.g. if a new size gets added to the catalog before this table is updated for it).
const EU_TO_PK_RANGE_TABLE = {
  '35-36': '2-3',
  '37-38': '4-5',
  '39-40': '6-7',
  '41-42': '7-8',
  '43-44': '9-10',
  '45-46': '10.5-11',
};
const EU_TO_PK_OFFSET = 33;
function euToPk(eu) {
  return eu - EU_TO_PK_OFFSET;
}

// The store's real size variants turned out to be authored as ranges ("35-36", "37-38"...), not
// the single EU numbers ("37", "38"...) originally tested against — confirmed directly, every
// clog colour's actual variant options are range strings. Looks the range up in the merchant's
// real chart first; only falls back to the euToPk formula (applied to each side of the range
// independently) for a range the table above doesn't cover yet.
function euRangeToPk(euRange) {
  const key = String(euRange).trim();
  if (EU_TO_PK_RANGE_TABLE[key]) return EU_TO_PK_RANGE_TABLE[key];
  return key.split('-').map((part) => {
    const n = Number(part.trim());
    return Number.isFinite(n) ? euToPk(n) : part.trim();
  }).join('-');
}

// For sorting a list of range strings ("35-36", "37-38"...) in real numeric order rather than
// lexicographic (which would still happen to work for two-digit ranges here, but breaks the
// moment a single-digit size ever exists) — pulls out just the first number in the string.
function euRangeSortKey(euRange) {
  const match = String(euRange).match(/-?\d+(\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

function initClogBuilder(root) {
  const dataEl = root.querySelector('[data-ccb-json]');
  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch (err) {
    // Fail loudly instead of hanging forever on "Loading preview…" with no clue why — this
    // almost always means the Liquid that builds this JSON emitted something invalid (e.g. a
    // stray trailing comma). Check the raw text logged below.
    const statusEl = root.querySelector('[data-ccb-status]');
    if (statusEl) statusEl.textContent = 'Builder failed to load — invalid config JSON (see console).';
    console.error('[custom-clog-builder] JSON.parse failed on data island:', err, dataEl.textContent);
    return;
  }

  // Real embedded 3D charm models (one GLB per "3D Charm model" block, each already baked with
  // the exact position/rotation/scale for one specific marker — see the block schema) take over
  // the whole Charms step when any are configured, replacing the flat-billboard system below
  // entirely rather than living alongside it. That's a deliberate interim state: for now there
  // are only as many real 3D charms as there are markers, so there's no "pick any of 100
  // designs" to offer yet — just a straight on/off toggle per available real model. The
  // billboard system (categories, quantity, hundreds of catalog designs) stays intact under
  // this and comes back automatically the moment no "3D Charm model" blocks are configured.
  const charm3DModels = Array.isArray(config.charm3DModels) ? config.charm3DModels : [];
  const is3DCharmMode = charm3DModels.length > 0;

  // Total charm *quantity* a customer can pick — not distinct charms; the same charm picked
  // twice counts twice, and is explicitly supported (see the quantity stepper below) even in
  // 3D-charm mode, where a repeat pick can't get its own second spot on the shoe (there's only
  // ever one real mesh per design, permanently tied to one marker) — a customer buying 2 of the
  // same real charm is still buying 2 real, separately-priced items, the preview just can't show
  // a design "twice" the way the flat billboards used to. Merchant-configurable via the same
  // "Max charms per clog" setting either way.
  const maxCharms = Number(config.maxCharms) || 6;

  // Extra strap pairs a customer can buy in addition to the one included strap colour — reuses
  // the exact same "Strap colour" products/variants as the main swatch row (no separate product
  // needed): each pair is just 2 more units of that colour's real variant added to the cart, so
  // it draws from the same inventory pool as an ordinary strap purchase. Price per pair is
  // therefore always exactly double that colour's own real unit price, computed here rather than
  // stored anywhere — there's no separate "pair" price to keep in sync with the real one.
  const spareStrapColors = Array.isArray(config.strapColors) ? config.strapColors : [];
  // How many of the selected charms actually render on the shoe — capped at the model's real
  // marker count once it loads (see buildCharmSlots' return value below). Starts equal to
  // maxCharms so nothing looks artificially limited before the model's loaded. In 3D-charm mode
  // this never actually changes from maxCharms: every *distinct* design shows at its own fixed
  // marker regardless of quantity, and there can never be more distinct designs selected than
  // there are real 3D models — so nothing is ever hidden there the way overflow billboards are.
  let previewSlotCount = maxCharms;

  // Charms are grouped into categories (one per real Shopify collection the merchant picked in
  // the theme editor — see the "Charm category" blocks in the section schema), not one flat
  // list. If the same product is listed in more than one category's collection, both category
  // tabs need to show it pointing at the *same* object — `selected.charm.includes(charm)`
  // (used throughout for selection state) is a reference check, so two separately-parsed copies
  // of "the same" charm would make it possible to select the product twice under two different
  // objects. Routing every category's charms through one productId → object map keeps a single
  // canonical instance no matter how many categories list it.
  // Per-step tab icon — an image the merchant uploads in the section settings (see
  // "stepIcons" in the JSON data island), keyed by the same step key used here. Purely
  // optional/decorative: a step with no icon configured just shows its number and label.
  const stepIcons = config.stepIcons || {};

  // Distinct EU sizes across every clog colour's real variants (a "Default Title" variant, on a
  // colour that hasn't had sizes added yet, has no numeric size and is skipped here) — this is
  // what populates the mandatory Size step, and it grows on its own as more colours get real
  // size variants, no separate merchant configuration needed. A size counts as available overall
  // if *any* colour currently has it in stock; a colour missing that exact size entirely just
  // falls back to its own default variant later (see resolveClogVariant) rather than being
  // treated as "this size doesn't exist".
  function buildSizeList(clogColors) {
    const bySize = new Map();
    (clogColors || []).forEach((color) => {
      (color.variants || []).forEach((v) => {
        // Real sizes here are range strings ("35-36"), not single numbers — "Default Title" (a
        // colour with no sizes yet) has no digits at all and is skipped the same way a bad
        // Number() parse used to be skipped.
        const eu = String(v.size || '').trim();
        if (!/\d/.test(eu)) return;
        const entry = bySize.get(eu) || { eu, pk: euRangeToPk(eu), available: false };
        if (v.available) entry.available = true;
        bySize.set(eu, entry);
      });
    });
    return Array.from(bySize.values()).sort((a, b) => euRangeSortKey(a.eu) - euRangeSortKey(b.eu));
  }

  const steps = STEP_DEFS
    .map((def) => {
      const icon = stepIcons[def.key] || null;
      if (def.key === 'size') return { ...def, items: buildSizeList(config.clogColors), icon };
      if (def.key !== 'charm') return { ...def, items: config[def.configKey] || [], icon };
      if (is3DCharmMode) {
        // 3D charms aren't grouped via separate "category" collection blocks like the billboard
        // ones are (see below) — each `charm_3d_model` block just carries its own plain-text
        // category name, since there's no real product collection backing a design made in bulk
        // by Meshy. Grouping happens client-side here instead, in first-seen order.
        const byId = new Map();
        const items = [];
        charm3DModels.forEach((c) => {
          if (byId.has(c.productId)) return;
          byId.set(c.productId, c);
          items.push(c);
        });
        const catOrder = [];
        const catMap = new Map();
        items.forEach((c) => {
          const name = c.category || 'Charms';
          if (!catMap.has(name)) { catMap.set(name, []); catOrder.push(name); }
          catMap.get(name).push(c);
        });
        const categories = catOrder.map((name) => ({ name, charms: catMap.get(name) }));
        return { ...def, items, categories, icon };
      }
      const rawCategories = config.charmCategories || [];
      const byId = new Map();
      const items = [];
      rawCategories.forEach((cat) => {
        (cat.charms || []).forEach((c) => {
          if (byId.has(c.productId)) return;
          // Tagged with its category name here (first-seen wins) so the order summary can read
          // charm.category the same way regardless of billboard vs. 3D-charm mode — 3D charms
          // already carry this field straight from their own block settings (see below).
          c.category = c.category || cat.name;
          byId.set(c.productId, c);
          items.push(c);
        });
      });
      const categories = rawCategories
        .map((cat) => ({ name: cat.name, charms: (cat.charms || []).map((c) => byId.get(c.productId)).filter(Boolean) }))
        .filter((cat) => cat.charms.length);
      return { ...def, items, categories, icon };
    })
    .filter((step) => step.items.length);

  if (!steps.length) {
    const statusEl = root.querySelector('[data-ccb-status]');
    if (statusEl) statusEl.textContent = 'No colours configured yet — add Clog/Strap colour blocks in the theme editor.';
    return;
  }

  const viewer = root.querySelector('[data-ccb-viewer]');
  const statusEl = root.querySelector('[data-ccb-status]');
  const tabsEl = root.querySelector('[data-ccb-tabs]');
  const mobileSheetEl = root.querySelector('[data-ccb-mobile-sheet]');
  const previewEl = root.querySelector('.ccb-preview');
  const sheetCloseBtn = root.querySelector('[data-ccb-sheet-close]');
  const stepLabelEl = root.querySelector('[data-ccb-step-label]');
  const selectionLineEl = root.querySelector('[data-ccb-selection-line]');
  const swatchRowEl = root.querySelector('[data-ccb-swatches]');
  const clogSizeNoteEl = root.querySelector('[data-ccb-clog-size-note]');
  const spareAddonEl = root.querySelector('[data-ccb-spare-addon]');
  const spareToggleBtn = root.querySelector('[data-ccb-spare-toggle]');
  const sparePanelEl = root.querySelector('[data-ccb-spare-panel]');
  const spareSummaryEl = root.querySelector('[data-ccb-spare-summary]');
  const spareGridEl = root.querySelector('[data-ccb-spare-grid]');
  const surpriseBtn = root.querySelector('[data-ccb-surprise]');
  const charmCardEl = root.querySelector('[data-ccb-charm-card]');
  const charmCategoriesEl = root.querySelector('[data-ccb-charm-categories]');
  const charmGridEl = root.querySelector('[data-ccb-charm-grid]');
  const charmShelfEl = root.querySelector('[data-ccb-charm-shelf]');
  const charmClearBtn = root.querySelector('[data-ccb-charm-clear]');
  const sizeUnitEl = root.querySelector('[data-ccb-size-unit]');
  const sizeGridEl = root.querySelector('[data-ccb-size-grid]');
  const sizeNoteEl = root.querySelector('[data-ccb-size-note]');
  const lockNoteEl = root.querySelector('[data-ccb-lock-note]');
  const totalEl = root.querySelector('[data-ccb-total]');
  const totalBarEl = root.querySelector('[data-ccb-total-bar]');
  const totalBarAmountEl = root.querySelector('[data-ccb-total-bar-amount]');
  const addBtn = root.querySelector('[data-ccb-add]');
  const addBtnNextEl = root.querySelector('[data-ccb-btn-next]');
  const addBtnCartEl = root.querySelector('[data-ccb-btn-cart]');
  const addBtnStatusEl = root.querySelector('[data-ccb-btn-status]');
  const fullscreenBtn = root.querySelector('[data-ccb-fullscreen]');
  const customizerViewEl = root.querySelector('[data-ccb-customizer-view]');
  const summaryViewEl = root.querySelector('[data-ccb-summary-view]');
  const summaryListEl = root.querySelector('[data-ccb-summary-list]');
  const summaryTotalEl = root.querySelector('[data-ccb-summary-total]');
  const backToCustomizerBtn = root.querySelector('[data-ccb-back-to-customizer]');
  const finalAddBtn = root.querySelector('[data-ccb-final-add]');
  const finalAddLabelEl = root.querySelector('[data-ccb-final-add-label]');
  const finalAddStatusEl = root.querySelector('[data-ccb-final-add-status]');

  const hasSizeStep = steps.some((step) => step.kind === 'size');
  // Clog/strap already have a default colour from the start, so "has a colour selected" can't be
  // what marks them done — they're ticked once the customer has actually dealt with the step:
  // picked a swatch, hit Surprise me, or moved on with Next (accepting the default). Size and
  // charms are different: size is done once picked, charms once at least one is added.
  const doneSteps = new Set();
  function isStepDone(step) {
    if (step.kind === 'size') return !!selected.size;
    if (step.kind === 'multi') return totalCharmQty() > 0;
    return doneSteps.has(step.key);
  }

  // Which unit the size grid displays as the *primary* (bold) label — "European" to match the
  // reference this was built from; the customer can flip it, doesn't change what's selected.
  let sizeUnit = 'eu';

  const selected = {}; // single steps: one item object. multi steps: array of item objects.
  steps.forEach((step) => {
    if (step.kind === 'multi') selected[step.key] = [];
    else if (step.kind === 'size') selected[step.key] = null;
    else {
      // Default to the first genuinely available option rather than blindly `items[0]` — a
      // merchant's first-listed colour might itself be completely sold out. Size isn't picked
      // yet at this point (the size step, earlier in STEP_DEFS, always sets its own selection to
      // null first), so this is a whole-colour stock check, not a per-size one.
      selected[step.key] = step.items.find((c) => isColorAvailable(step.key, c)) || step.items[0];
    }
  });
  // Not a step of its own (it's an add-on inside the Strap step), so it isn't covered by the
  // loop above — same {item, qty} shape as the charm step's entries, duplicates allowed.
  selected.spareStraps = [];
  let activeStepIndex = 0;
  let activeCharmCategoryIndex = 0;
  // Collapsed by default — matches every other optional add-on pattern in this builder (nothing
  // opens itself uninvited); stays open/closed as the customer navigates away from and back to
  // the Strap step, rather than resetting every time.
  let spareStrapsOpen = false;

  // The chosen clog colour's variant that actually matches the chosen size, when one exists and
  // is in stock — falling back to that colour's own default variant otherwise (a colour that
  // hasn't had sizes added yet, or is simply out of the specific size picked). This is the single
  // source of truth for what the "Clog" line item's price/SKU actually is; both currentTotal()
  // and the cart submission use it rather than reading selected.clog directly.
  function resolveClogVariant() {
    const color = selected.clog;
    if (!color) return null;
    if (selected.size && Array.isArray(color.variants)) {
      const match = color.variants.find((v) => String(v.size).trim() === selected.size.eu && v.available);
      if (match) return { variantId: match.variantId, price: match.price, sizeMatched: true };
    }
    return { variantId: color.variantId, price: color.price, sizeMatched: false };
  }

  // Straps are sized to match the clog (each strap colour's product has one variant per size,
  // same shape as the clog products) — this mirrors resolveClogVariant() exactly so the size the
  // customer picked for the clog is what actually gets deducted from strap inventory too, instead
  // of every order silently drawing from whichever variant Shopify treats as that product's
  // default regardless of size.
  function resolveStrapVariant() {
    const color = selected.strap;
    if (!color) return null;
    if (selected.size && Array.isArray(color.variants)) {
      const match = color.variants.find((v) => String(v.size).trim() === selected.size.eu && v.available);
      if (match) return { variantId: match.variantId, price: match.price, sizeMatched: true };
    }
    return { variantId: color.variantId, price: color.price, sizeMatched: false };
  }

  // Whether this colour (clog or strap — both are sized the same way) has genuine stock at all,
  // independent of size — used before a size is picked (or for a colour that's simply completely
  // sold out) rather than the exact-size check below. A colour with no `variants` array at all
  // hasn't had sizes configured yet, which (unlike a real colour whose every size variant is
  // explicitly marked unavailable) isn't the same as "sold out" — it just hasn't been split into
  // per-size stock yet, so it stays selectable until it has.
  function clogColorHasAnyStock(color) {
    if (!Array.isArray(color.variants) || !color.variants.length) return true;
    return color.variants.some((v) => v.available);
  }

  // Whether this colour (clog or strap) specifically has the customer's chosen size in stock.
  // Unlike resolveClogVariant()/resolveStrapVariant() (which silently fall back to the colour's
  // own default variant so a price/SKU is always resolvable for the cart), this is the strict
  // check used to decide whether the swatch itself should even be selectable — a colour that
  // hasn't had this size added yet, or has it marked out of stock, shouldn't let a customer pick
  // it and quietly get swapped to a different size instead.
  function clogColorAvailableForSize(color, size) {
    if (!size) return clogColorHasAnyStock(color);
    if (!Array.isArray(color.variants) || !color.variants.length) return false;
    const match = color.variants.find((v) => String(v.size).trim() === size.eu);
    return !!(match && match.available);
  }

  // Single entry point both the swatch renderer and the default/auto-reassigned selection use —
  // clog and strap colours are both sized (see resolveStrapVariant() above) so both depend on
  // whichever size is currently chosen; charms are the only thing left using the flat `available`
  // flag directly (defaulting true if a hand-edited config predates that field).
  function isColorAvailable(stepKey, color) {
    if (stepKey === 'clog' || stepKey === 'strap') return clogColorAvailableForSize(color, selected.size);
    return color.available !== false;
  }

  function money(n) {
    return config.currencySymbol + Number(n).toLocaleString();
  }

  // What one spare-strap entry costs: the strap products are already priced per *pair*, so a spare
  // pair is one unit of the size-matched strap price (same matching resolveStrapVariant() does
  // for the included strap — which is also one unit, i.e. one pair). Shared by the running total
  // and the summary rows so the two can never disagree.
  function spareEntryPrice(entry) {
    const match = selected.size && Array.isArray(entry.color.variants)
      ? entry.color.variants.find((v) => String(v.size).trim() === selected.size.eu && v.available)
      : null;
    const price = match ? match.price : entry.color.price;
    return Number(price) * entry.qty;
  }

  // `throughStepIndex` limits the sum to the steps up to and including that one, so the running
  // total on screen is cumulative as the customer works through the builder (Clog step: just the
  // clog; Strap step: clog + strap; Charms step: everything) instead of already counting the
  // default strap while they're still choosing the clog. The cart total (the order summary) always
  // passes nothing, i.e. counts every step.
  function currentTotal(throughStepIndex = steps.length - 1) {
    const stepsTotal = steps.reduce((sum, step, stepIndex) => {
      if (stepIndex > throughStepIndex) return sum;
      if (step.kind === 'size') return sum; // no price of its own — folded into the clog variant's price
      if (step.kind === 'multi') {
        return sum + selected[step.key].reduce((s, entry) => s + Number(entry.charm.price) * entry.qty, 0);
      }
      if (step.key === 'clog') {
        const rv = resolveClogVariant();
        return sum + Number(rv ? rv.price : 0);
      }
      if (step.key === 'strap') {
        const rv = resolveStrapVariant();
        return sum + Number(rv ? rv.price : 0);
      }
      return sum + Number(selected[step.key].price);
    }, 0);
    // Not a step, so it isn't covered by the reduce above — added on separately. Each entry.qty
    // is pairs, and a pair is 1 unit of the size-matched strap price (the strap products are
    // priced per pair already; see resolveStrapVariant() above). Spare pairs are meant
    // to match the clog size too (same reasoning as the included strap), not the colour's flat
    // default price/variant.
    // They're picked on the Strap step, so they only count once that step has been reached.
    const strapStepIndex = steps.findIndex((step) => step.key === 'strap');
    const spareStrapsTotal = strapStepIndex === -1 || strapStepIndex <= throughStepIndex
      ? selected.spareStraps.reduce((sum, entry) => sum + spareEntryPrice(entry), 0)
      : 0;
    return stepsTotal + spareStrapsTotal;
  }

  // Total charm units selected (the same charm picked 3 times counts as 3), as opposed to
  // `selected.charm.length`, which is the number of *distinct* charms picked — the cap below is
  // on total units, matching "pick up to N charms" the way a customer reads it (quantities and
  // all), not "pick up to N different designs".
  function totalCharmQty() {
    return selected.charm.reduce((sum, entry) => sum + entry.qty, 0);
  }

  // Below this width the CSS turns .ccb-mobile-sheet into a real bottom sheet (display:contents
  // above it, so opening/closing it there would be a no-op) — checked live rather than cached
  // since a phone can rotate or a window can resize without a full reload.
  function isMobileLayout() {
    return window.matchMedia('(max-width: 767px)').matches;
  }
  // No dimming overlay: instead of the sheet covering the 3D preview, the preview shrinks to fit
  // the space above the sheet (`is-sheet-open` on the root drives that in the CSS) and grows back
  // when the sheet closes, so the shoe stays fully visible and draggable while a step is open.
  // The preview is scrolled to the top of the screen on open — the CSS sizes it assuming its top
  // edge sits at the top of the viewport, so it ends exactly where the sheet begins.
  // The class flip on its own, with no scrolling — used for the open-by-default states below,
  // where the caller already controls where the page is scrolled.
  function expandMobileSheet() {
    if (!isMobileLayout()) return;
    mobileSheetEl.classList.add('is-open');
    root.classList.add('is-sheet-open');
    // The preview's open-state height is derived from this variable, and the ResizeObserver below
    // only reports it a frame later — set it right now so the very first open frame is already
    // sized to the space above the drawer instead of briefly running underneath it.
    root.style.setProperty('--ccb-sheet-h', `${mobileSheetEl.offsetHeight}px`);
  }
  function openMobileSheet() {
    expandMobileSheet();
  }
  function closeMobileSheet() {
    mobileSheetEl.classList.remove('is-open');
    root.classList.remove('is-sheet-open');
  }
  sheetCloseBtn.addEventListener('click', closeMobileSheet);

  // The sheet's height changes per step (size grid vs. swatches vs. the charm grid), and the
  // preview's open-state height is derived from it — kept as a CSS variable so it follows
  // automatically instead of every render path having to remember to resync it.
  new ResizeObserver(() => {
    root.style.setProperty('--ccb-sheet-h', `${mobileSheetEl.offsetHeight}px`);
    syncPreviewTop();
  }).observe(mobileSheetEl);

  function renderTabs() {
    tabsEl.innerHTML = '';
    steps.forEach((step, i) => {
      // Size is a mandatory first step when it's configured at all — every other tab is locked
      // (visibly greyed, not just inert) until a size is picked, rather than a strict one-at-a-
      // time wizard: once size is chosen every other step opens up together, same free
      // navigation between them as before this existed.
      const locked = hasSizeStep && step.kind !== 'size' && !selected.size;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ccb-tab' + (i === activeStepIndex ? ' is-active' : '') + (locked ? ' is-locked' : '');

      const num = document.createElement('span');
      num.className = 'ccb-tab-number';
      num.textContent = String(i + 1);
      btn.appendChild(num);

      if (step.icon) {
        const icon = document.createElement('img');
        icon.className = 'ccb-tab-icon';
        icon.src = step.icon;
        icon.alt = '';
        btn.appendChild(icon);
      }

      const label = document.createElement('span');
      label.className = 'ccb-tab-label';
      label.textContent = step.tabLabel;
      btn.appendChild(label);

      if (isStepDone(step)) {
        const check = document.createElement('span');
        check.className = 'ccb-tab-check';
        check.textContent = '✓';
        btn.appendChild(check);
      }
      btn.addEventListener('click', () => {
        if (locked) return;
        activeStepIndex = i;
        renderTabs();
        renderStep();
        openMobileSheet();
      });
      tabsEl.appendChild(btn);

      // A "+" between pills, not after the last one — purely visual (build "Clog + Strap +
      // Charms"), so it's marked decorative rather than something a screen reader announces.
      if (i < steps.length - 1) {
        const plus = document.createElement('span');
        plus.className = 'ccb-tab-plus';
        plus.textContent = '+';
        plus.setAttribute('aria-hidden', 'true');
        tabsEl.appendChild(plus);
      }
    });
  }

  function renderStep() {
    const step = steps[activeStepIndex];
    stepLabelEl.textContent = `Step ${activeStepIndex + 1}: ${step.stepText}`;

    // Hide every step's own UI by default, then only the branch below un-hides what it needs —
    // simpler than each branch remembering to hide every *other* step's elements.
    sizeUnitEl.classList.add('ccb-hidden');
    sizeGridEl.classList.add('ccb-hidden');
    sizeNoteEl.classList.add('ccb-hidden');
    swatchRowEl.classList.add('ccb-hidden');
    clogSizeNoteEl.classList.add('ccb-hidden');
    spareAddonEl.classList.add('ccb-hidden');
    surpriseBtn.classList.add('ccb-hidden');
    charmCardEl.classList.add('ccb-hidden');
    charmCategoriesEl.classList.add('ccb-hidden');
    charmGridEl.classList.add('ccb-hidden');
    charmShelfEl.classList.add('ccb-hidden');
    charmClearBtn.classList.add('ccb-hidden');
    totalBarEl.classList.toggle('ccb-hidden', step.kind === 'size');

    if (step.kind === 'size') {
      sizeUnitEl.classList.remove('ccb-hidden');
      sizeGridEl.classList.remove('ccb-hidden');
      selectionLineEl.textContent = selected.size ? `Size: ${formatSize(selected.size)}` : 'Pick your size to continue';
      renderSizeUnitToggle();
      renderSizeGrid(step);
    } else if (step.kind === 'single') {
      swatchRowEl.classList.remove('ccb-hidden');

      selectionLineEl.innerHTML = `Colour: <strong>${selected[step.key].name}</strong><span class="ccb-color-check">✓</span>`;

      swatchRowEl.innerHTML = '';
      let anyDisabled = false;
      step.items.forEach((c) => {
        const available = isColorAvailable(step.key, c);
        if (!available) anyDisabled = true;

        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'ccb-swatch' + (selected[step.key] === c ? ' is-active' : '') + (!available ? ' is-disabled' : '');
        el.style.background = c.hex;
        el.disabled = !available;
        el.title = available
          ? c.name
          : ((step.key === 'clog' || step.key === 'strap') && selected.size ? `${c.name} — out of stock in size ${formatSize(selected.size)}` : `${c.name} — out of stock`);
        if (available) {
          el.addEventListener('click', () => {
            selected[step.key] = c;
            doneSteps.add(step.key);
            setPartColor(step.key, c.hex);
            renderStep();
            renderTotal();
          });
        }
        swatchRowEl.appendChild(el);
      });

      // Same convention as the Size step's own "Greyed-out sizes are currently sold out." note —
      // shown whenever at least one swatch here is actually disabled, using this element for both
      // the clog and strap steps rather than adding a near-identical second one.
      if (anyDisabled) {
        clogSizeNoteEl.textContent = (step.key === 'clog' || step.key === 'strap') && selected.size
          ? `Greyed-out colours don't have size ${formatSize(selected.size)} in stock.`
          : 'Greyed-out colours are currently out of stock.';
        clogSizeNoteEl.classList.remove('ccb-hidden');
      } else if ((step.key === 'clog' || step.key === 'strap') && selected.size) {
        // Defensive only — should be unreachable in practice, since a size only ever shows as
        // pickable at all (see buildSizeList) if some clog colour has it, and the size-grid click
        // handler keeps selected.clog/selected.strap in sync with whichever size is chosen. Kept
        // as a plain, visible message rather than letting resolveClogVariant()'s/
        // resolveStrapVariant()'s cart-safety fallback quietly substitute a different size with no
        // indication anything was off.
        const rv = step.key === 'clog' ? resolveClogVariant() : resolveStrapVariant();
        if (rv && !rv.sizeMatched) {
          clogSizeNoteEl.textContent = `No ${step.key} colour currently has size ${formatSize(selected.size)} in stock.`;
          clogSizeNoteEl.classList.remove('ccb-hidden');
        }
      }

      // "Surprise me" lives on the Clog step specifically (that's where it's reachable first),
      // but randomises both Clog and Strap together — a customer picking one color-blind combo
      // over another is really choosing "the outfit", not just the base on its own.
      if (step.key === 'clog') surpriseBtn.classList.remove('ccb-hidden');

      // Spare straps only ever show alongside the included strap colour's own swatch row.
      if (step.key === 'strap') renderSpareStrapAddon();
    } else {
      charmCardEl.classList.remove('ccb-hidden');
      charmGridEl.classList.remove('ccb-hidden');

      const count = totalCharmQty();
      selectionLineEl.textContent = count ? `${count} of ${maxCharms} selected` : `Pick up to ${maxCharms} charms`;

      // Only worth mentioning when it actually limits anything — a merchant whose max is at or
      // below the real marker count never hits this, so nothing here would ever be true for them.
      // Reuses the same banner element the clog/strap steps use for their own stock notes (hidden
      // by default at the top of renderStep, unused during the charm step otherwise).
      if (maxCharms > previewSlotCount) {
        clogSizeNoteEl.textContent = `Preview of ${previewSlotCount} charms available only. The rest will be added to cart automatically.`;
        clogSizeNoteEl.classList.remove('ccb-hidden');
      }

      renderCharmCategories(step);
      renderCharmGrid(step);
      renderCharmShelf();
    }

    // Mandatory gate: no adding to cart (or, per renderTabs above, even looking at the other
    // steps) until a size is picked, whenever this store has a Size step configured at all.
    // The button itself stays enabled/clickable even while locked — it reads "Select a Size" at
    // that point (see below), so greying it out on top of that read as broken rather than as
    // guidance. The click handler's own early-return is what actually blocks advancing.
    const locked = hasSizeStep && !selected.size;
    lockNoteEl.classList.toggle('ccb-hidden', !locked);

    // Charms is the only genuinely optional step (Size/Clog/Strap all need an explicit pick, or
    // already default to one) — the button reads "Next" and just advances through those,
    // becoming the real Add to Cart action only once the customer reaches Charms. Status
    // (Adding…/Added/error) only ever shows mid-submission — any ordinary step render goes back
    // to the plain next/cart pair.
    const isLastStep = activeStepIndex === steps.length - 1;
    addBtnNextEl.classList.toggle('ccb-hidden', isLastStep);
    addBtnCartEl.classList.toggle('ccb-hidden', !isLastStep);
    if (!isLastStep) {
      // The button is disabled here anyway (see `locked` above), but "Next: Clog" while nothing
      // is actually pickable yet reads like the button works and just does nothing — naming the
      // real blocker instead matches the lock note already shown beneath it.
      addBtnNextEl.textContent = locked ? 'Select a Size' : `Next: ${steps[activeStepIndex + 1].tabLabel}`;
    }
    addBtnStatusEl.classList.add('ccb-hidden');

    // Whatever just changed (a swatch, a charm, the size) may have completed or un-completed a
    // step, so the ticks on the tabs are refreshed on every step render rather than every
    // individual handler having to remember to.
    renderTabs();
    // The running total follows the active step, so moving between steps must refresh it too.
    renderTotal();
  }

  function formatSize(size) {
    return sizeUnit === 'pk' ? `PK ${size.pk}` : String(size.eu);
  }

  function renderSizeUnitToggle() {
    sizeUnitEl.innerHTML = '';
    [['pk', 'Pakistani'], ['eu', 'European']].forEach(([unit, label]) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ccb-size-unit-btn' + (sizeUnit === unit ? ' is-active' : '');
      btn.textContent = label;
      btn.addEventListener('click', () => {
        if (sizeUnit === unit) return;
        sizeUnit = unit;
        renderSizeUnitToggle();
        renderSizeGrid(steps[activeStepIndex]);
      });
      sizeUnitEl.appendChild(btn);
    });
  }

  function renderSizeGrid(step) {
    sizeGridEl.innerHTML = '';
    step.items.forEach((size) => {
      const el = document.createElement('button');
      el.type = 'button';
      const isSelected = selected.size && selected.size.eu === size.eu;
      el.className = 'ccb-size-tile' + (isSelected ? ' is-active' : '') + (!size.available ? ' is-disabled' : '');
      el.disabled = !size.available;

      const primary = document.createElement('span');
      primary.className = 'ccb-size-primary';
      primary.textContent = sizeUnit === 'pk' ? `PK ${size.pk}` : String(size.eu);

      const secondary = document.createElement('span');
      secondary.className = 'ccb-size-secondary';
      secondary.textContent = sizeUnit === 'pk' ? String(size.eu) : `PK ${size.pk}`;

      el.appendChild(primary);
      el.appendChild(secondary);
      el.addEventListener('click', () => {
        if (!size.available) return;
        selected.size = size;
        // The clog/strap colour picked under a previous size may not carry this one — swap each
        // to the first colour that does rather than leaving an invalid selection in place
        // (surfaced only via resolveClogVariant's/resolveStrapVariant's silent fallback otherwise).
        ['clog', 'strap'].forEach((key) => {
          const matchingStep = steps.find((s) => s.key === key);
          if (matchingStep && selected[key] && !clogColorAvailableForSize(selected[key], selected.size)) {
            const fallback = matchingStep.items.find((c) => clogColorAvailableForSize(c, selected.size));
            if (fallback) {
              selected[key] = fallback;
              setPartColor(key, fallback.hex);
            }
          }
        });
        renderTabs();
        renderStep();
        renderTotal();
      });
      sizeGridEl.appendChild(el);
    });

    const anySoldOut = step.items.some((s) => !s.available);
    sizeNoteEl.classList.toggle('ccb-hidden', !anySoldOut);
    if (anySoldOut) sizeNoteEl.textContent = 'Greyed-out sizes are currently sold out.';
  }

  // A compact running summary of every charm unit added so far — up to `maxCharms` tiles, one
  // per unit already selected (in the order they were added, same order the 3D preview slots
  // fill in) followed by dashed placeholders for whatever's left of the cap. Exists alongside
  // the grid above rather than replacing it: the grid is "what's available", this is "what's in
  // the build so far" — with 100 catalog designs (or even just several categories), scrolling
  // back through the grid to see what's already been picked isn't practical.
  function renderCharmShelf() {
    const units = flattenedCharmUnits();
    if (!units.length && !maxCharms) { charmShelfEl.classList.add('ccb-hidden'); charmClearBtn.classList.add('ccb-hidden'); return; }
    charmShelfEl.classList.remove('ccb-hidden');
    charmClearBtn.classList.toggle('ccb-hidden', units.length === 0);
    charmShelfEl.innerHTML = '';
    units.forEach((charm) => {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'ccb-shelf-tile';
      tile.title = `Remove one ${charm.name}`;
      if (charm.image) {
        const img = document.createElement('img');
        img.src = charm.image;
        img.alt = charm.name;
        tile.appendChild(img);
      }
      // Removes just one unit of *this* charm, not the whole entry — clicking one tile of a
      // charm picked 3 times takes it down to 2, matching what tapping "−" in the grid does.
      tile.addEventListener('click', () => decrementCharm(charm));
      charmShelfEl.appendChild(tile);
    });
    for (let i = units.length; i < maxCharms; i++) {
      const empty = document.createElement('div');
      empty.className = 'ccb-shelf-tile ccb-shelf-tile-empty';
      empty.textContent = '+';
      charmShelfEl.appendChild(empty);
    }
  }

  // Category pills (one per "Charm category" block/collection the merchant configured) sit
  // above the grid, matching the Figma. A merchant who hasn't set any up yet still gets exactly
  // today's single flat grid — `step.categories` is only populated when there's real category
  // data (see the STEP_DEFS mapping above), so this row just stays hidden in that case rather
  // than showing a single meaningless "all charms" pill.
  function renderCharmCategories(step) {
    const categories = step.categories || [];
    if (categories.length < 2) {
      charmCategoriesEl.classList.add('ccb-hidden');
      charmCategoriesEl.innerHTML = '';
      return;
    }
    if (activeCharmCategoryIndex >= categories.length) activeCharmCategoryIndex = 0;
    charmCategoriesEl.classList.remove('ccb-hidden');
    charmCategoriesEl.innerHTML = '';
    categories.forEach((cat, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ccb-charm-category' + (i === activeCharmCategoryIndex ? ' is-active' : '');
      btn.textContent = cat.name;
      btn.addEventListener('click', () => {
        if (activeCharmCategoryIndex === i) return;
        activeCharmCategoryIndex = i;
        renderCharmCategories(step);
        renderCharmGrid(step);
      });
      charmCategoriesEl.appendChild(btn);
    });
  }

  function renderCharmGrid(step) {
    const categories = step.categories || [];
    const pool = categories.length >= 2 ? (categories[activeCharmCategoryIndex] || categories[0]).charms : step.items;

    charmGridEl.innerHTML = '';
    const totalQty = totalCharmQty();
    const atCap = totalQty >= maxCharms;

    pool.forEach((charm) => {
      const entry = findCharmEntry(charm);
      const qty = entry ? entry.qty : 0;
      const isSelected = qty > 0;
      // Sold-out charms stay visible (so the range doesn't look like it shrank) but can't be
      // picked — otherwise the whole build is rejected at "Add to Cart" with a 422.
      const soldOut = !isCharmInStock(charm);

      const el = document.createElement('div');
      el.className = 'ccb-charm-item' + (isSelected ? ' is-selected' : '');

      const thumbBtn = document.createElement('button');
      thumbBtn.type = 'button';
      thumbBtn.className = 'ccb-charm-thumb-btn' + (soldOut ? ' is-disabled is-sold-out' : (!isSelected && atCap ? ' is-disabled' : ''));
      thumbBtn.title = soldOut ? charm.name + ' — sold out' : charm.name;
      thumbBtn.disabled = soldOut || (!isSelected && atCap);

      const thumb = document.createElement('div');
      thumb.className = 'ccb-charm-thumb';
      if (charm.image) {
        const img = document.createElement('img');
        img.src = charm.image;
        img.loading = 'lazy';
        img.alt = charm.name;
        thumb.appendChild(img);
      }
      if (isSelected) {
        const check = document.createElement('span');
        check.className = 'ccb-charm-check';
        check.textContent = '✓';
        thumb.appendChild(check);
      }
      thumbBtn.appendChild(thumb);
      // Clicking the image itself while selected removes it outright, quantity and all — a
      // quick "never mind" that doesn't require dialing the stepper back down one at a time.
      thumbBtn.addEventListener('click', () => (isSelected ? setCharmQty(charm, 0) : incrementCharm(charm)));
      el.appendChild(thumbBtn);

      if (isSelected) {
        const qtyRow = document.createElement('div');
        qtyRow.className = 'ccb-charm-qty';

        const dec = document.createElement('button');
        dec.type = 'button';
        dec.className = 'ccb-qty-btn';
        dec.textContent = '−';
        dec.setAttribute('aria-label', 'Decrease quantity');
        dec.addEventListener('click', () => decrementCharm(charm));

        const val = document.createElement('span');
        val.className = 'ccb-qty-value';
        val.textContent = String(qty);

        const inc = document.createElement('button');
        inc.type = 'button';
        inc.className = 'ccb-qty-btn';
        inc.textContent = '+';
        inc.setAttribute('aria-label', 'Increase quantity');
        inc.disabled = atCap || qty >= charmStockLimit(charm);
        inc.addEventListener('click', () => incrementCharm(charm));

        qtyRow.appendChild(dec);
        qtyRow.appendChild(val);
        qtyRow.appendChild(inc);
        el.appendChild(qtyRow);
      }

      charmGridEl.appendChild(el);
    });
  }

  function findCharmEntry(charm) {
    return selected.charm.find((entry) => entry.charm === charm);
  }

  function setCharmQty(charm, qty) {
    const entry = findCharmEntry(charm);
    if (qty <= 0) {
      if (entry) selected.charm.splice(selected.charm.indexOf(entry), 1);
    } else if (entry) {
      entry.qty = qty;
    } else {
      selected.charm.push({ charm, qty });
    }
    assignCharmSlots();
    renderStep();
    renderTotal();
  }

  // `available` / `maxQty` come from the section's data island (flat variant stock): `available`
  // is false once the product has no sellable units, `maxQty` is the units left when the store
  // tracks inventory and refuses oversells (null = no known limit).
  function isCharmInStock(charm) {
    return charm.available !== false && charmStockLimit(charm) > 0;
  }
  function charmStockLimit(charm) {
    return typeof charm.maxQty === 'number' ? charm.maxQty : Infinity;
  }

  function incrementCharm(charm) {
    if (totalCharmQty() >= maxCharms) return;
    const entry = findCharmEntry(charm);
    const nextQty = (entry ? entry.qty : 0) + 1;
    if (!isCharmInStock(charm) || nextQty > charmStockLimit(charm)) return;
    setCharmQty(charm, nextQty);
  }

  function decrementCharm(charm) {
    const entry = findCharmEntry(charm);
    if (!entry) return;
    setCharmQty(charm, entry.qty - 1);
  }

  // The running total follows the step the customer is on. The Size step has no price of its own,
  // so it shows the clog's price rather than a meaningless 0.
  function renderTotal() {
    pruneSpareStraps();
    const firstPricedStepIndex = Math.max(0, steps.findIndex((step) => step.kind !== 'size'));
    const running = currentTotal(Math.max(activeStepIndex, firstPricedStepIndex));
    totalEl.textContent = money(running);
    totalBarAmountEl.textContent = money(running);
  }

  // One click empties the whole charm selection instead of toggling every tile off one at a
  // time — the 3D preview updates in this same pass, not charm-by-charm as each old toggle
  // click used to trigger.
  charmClearBtn.addEventListener('click', () => {
    if (!selected.charm.length) return;
    selected.charm.length = 0;
    assignCharmSlots();
    renderStep();
    renderTotal();
  });

  // Randomises Clog and Strap together, not just whichever one the customer happens to be
  // looking at — matches the swatch click's own update path (selected[...] + setPartColor)
  // exactly, so this can never drift out of sync with picking a colour by hand.
  function pickRandom(list) {
    return list[Math.floor(Math.random() * list.length)];
  }
  surpriseBtn.addEventListener('click', () => {
    ['clog', 'strap'].forEach((key) => {
      const matchingStep = steps.find((s) => s.key === key);
      if (!matchingStep || !matchingStep.items.length) return;
      // Never surprise the customer with an out-of-stock colour — same availability check the
      // swatch row itself uses, so "Surprise me" can't land on something it would then grey out.
      const pickable = matchingStep.items.filter((c) => isColorAvailable(key, c));
      const choice = pickRandom(pickable.length ? pickable : matchingStep.items);
      selected[key] = choice;
      doneSteps.add(key);
      setPartColor(key, choice.hex);
    });
    renderStep();
    renderTotal();
  });

  // --- Spare straps (optional add-on, Strap step only) ---
  // Total spare pairs across every colour.
  function totalSpareQty() {
    return selected.spareStraps.reduce((sum, entry) => sum + entry.qty, 0);
  }
  function findSpareEntry(color) {
    return selected.spareStraps.find((entry) => entry.color === color);
  }
  function setSpareQty(color, qty) {
    const entry = findSpareEntry(color);
    if (qty <= 0) {
      if (entry) selected.spareStraps.splice(selected.spareStraps.indexOf(entry), 1);
    } else if (entry) {
      entry.qty = qty;
    } else {
      selected.spareStraps.push({ color, qty });
    }
    renderSpareStrapAddon();
    renderTotal();
  }
  // One spare pair per colour — tapping a colour adds it, tapping it again takes it off. No
  // quantities; different colours can still be combined.
  function toggleSpare(color) {
    if (color === selected.strap) return; // already included with the clog — see isMainStrapColor
    setSpareQty(color, findSpareEntry(color) ? 0 : 1);
  }
  // A spare pair in the same colour as the strap that already comes with the clog is pointless, so
  // that colour is disabled in the spare grid. The reverse also has to hold: a customer can pick a
  // spare colour first and then change the main strap *to* it (or "Surprise me" / a size change can
  // do it for them), so any spare pair that now matches the main strap is dropped.
  function isMainStrapColor(color) {
    return color === selected.strap;
  }
  function pruneSpareStraps() {
    for (let i = selected.spareStraps.length - 1; i >= 0; i--) {
      if (isMainStrapColor(selected.spareStraps[i].color)) selected.spareStraps.splice(i, 1);
    }
  }

  function renderSpareStrapAddon() {
    // In practice this only happens if the Strap step itself has no colours configured, in which
    // case the step doesn't exist at all and this never gets called — kept as a safety net rather
    // than assuming that invariant always holds.
    if (!spareStrapColors.length) {
      spareAddonEl.classList.add('ccb-hidden');
      return;
    }
    spareAddonEl.classList.remove('ccb-hidden');

    pruneSpareStraps();
    const totalQty = totalSpareQty();
    spareSummaryEl.textContent = totalQty
      ? `${totalQty} pair${totalQty === 1 ? '' : 's'} added`
      : 'Extra pairs in any colour';

    spareToggleBtn.setAttribute('aria-expanded', String(spareStrapsOpen));
    spareToggleBtn.classList.toggle('is-open', spareStrapsOpen);
    sparePanelEl.classList.toggle('ccb-hidden', !spareStrapsOpen);
    if (!spareStrapsOpen) return; // grid only needs building while the panel is actually visible

    spareGridEl.innerHTML = '';
    spareStrapColors.forEach((color) => {
      const isSelected = !!findSpareEntry(color);

      // A spare pair is meant to match the clog size too (see resolveStrapVariant()) — a colour
      // that's out of stock in that size shouldn't be offered as a spare any more than it's
      // offered as the included strap itself. No cap on quantity, so stock is the only thing that
      // can disable a colour here.
      const outOfStock = !isColorAvailable('strap', color);
      const isMainStrap = isMainStrapColor(color);
      const disabled = isMainStrap || (!isSelected && outOfStock);

      const item = document.createElement('div');
      item.className = 'ccb-spare-item' + (isSelected ? ' is-selected' : '');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ccb-spare-swatch-btn' + (disabled ? ' is-disabled' : '');
      btn.title = isMainStrap
        ? `${color.name} — already your strap colour`
        : outOfStock
          ? (selected.size ? `${color.name} — out of stock in size ${formatSize(selected.size)}` : `${color.name} — out of stock`)
          : color.name;
      btn.disabled = disabled;
      btn.addEventListener('click', () => toggleSpare(color));

      const swatch = document.createElement('span');
      swatch.className = 'ccb-spare-swatch';
      swatch.style.background = color.hex;
      btn.appendChild(swatch);

      const name = document.createElement('span');
      name.className = 'ccb-spare-name';
      name.textContent = color.name;
      btn.appendChild(name);

      item.appendChild(btn);

      spareGridEl.appendChild(item);
    });
  }

  spareToggleBtn.addEventListener('click', () => {
    spareStrapsOpen = !spareStrapsOpen;
    renderSpareStrapAddon();
  });

  // --- Three.js scene setup ---
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf4f3f6);

  // fov=20 was tuned by eye against the desktop preview box, which is a 1:1 square (aspect 1) —
  // PerspectiveCamera's fov is the VERTICAL field of view, so on a narrower/taller box (the mobile
  // bottom-sheet preview, aspect < 1) the same vertical fov leaves a much narrower horizontal fov,
  // which reads as "zoomed in" left-to-right even though nothing about the camera's distance or
  // bearing changed. fovForAspect keeps the horizontal framing pinned to what it is at aspect 1,
  // growing the vertical fov (not the camera's distance) to compensate on portrait containers —
  // desktop's square box is unaffected (aspect >= 1 there, so this is a no-op).
  const BASE_FOV = 20;
  const BASE_ASPECT = 1;
  function fovForAspect(aspect) {
    if (aspect >= BASE_ASPECT) return BASE_FOV;
    const baseVFovRad = THREE.MathUtils.degToRad(BASE_FOV);
    const baseHFovRad = 2 * Math.atan(Math.tan(baseVFovRad / 2) * BASE_ASPECT);
    const newVFovRad = 2 * Math.atan(Math.tan(baseHFovRad / 2) / aspect);
    return THREE.MathUtils.radToDeg(newVFovRad);
  }

  const camera = new THREE.PerspectiveCamera(20, viewer.clientWidth / viewer.clientHeight, 0.01, 10);
  camera.fov = fovForAspect(camera.aspect);
  camera.updateProjectionMatrix();
  camera.position.copy(DEFAULT_VIEW_POINT);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  // Phones report a pixel ratio of 3 and the cap used to be 2; at 1.5 the preview is still sharp
  // at that size but costs roughly half the fill work every frame, which is what keeps drags and
  // step changes smooth on mid-range devices. Desktop keeps the full 2x.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobileLayout() ? 1.5 : 2));
  renderer.setSize(viewer.clientWidth, viewer.clientHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.shadowMap.enabled = true;
  viewer.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  // Aimed at the toe box specifically (where the charm markers actually are), not the heel —
  // see the note on DEFAULT_VIEW_POINT above for how the earlier version ended up on the wrong
  // side of the shoe.
  controls.target.set(0, 0.075, -0.08);
  controls.enableDamping = true;
  controls.minDistance = 0.15;
  controls.maxDistance = 1.2;
  if (is3DCharmMode) {
    // Real embedded 3D charm meshes read correctly from any angle — checked directly across a
    // full orbit — because they're actual geometry sitting flush on the surface, not a flat
    // image forced to face one fixed point. None of the reasoning below (which is specific to
    // the billboard system) applies here, so rotation stays fully free.
    controls.enableRotate = true;
    controls.enablePan = false;
    // Spinning left/right stays free, but the camera can't dip below the shoe: the polar angle
    // is measured from straight up (0 = top-down, 90° = level with the orbit target), so capping
    // it just under 90° keeps the camera above the sole plane and the underside never shows. The
    // small minimum stops a perfectly top-down view, where the orbit flips awkwardly.
    controls.minPolarAngle = THREE.MathUtils.degToRad(12);
    controls.maxPolarAngle = THREE.MathUtils.degToRad(84);
    controls.update();
  } else {
    // Full 360° rotation is locked out on purpose. Every charm's real local surface normal
    // points a genuinely different direction (measured directly — some barely 25° off the
    // camera, others past 70°), because the marker positions sit across a shoe surface that
    // curves a lot over a few centimetres. There is no single orientation rule that reads
    // clearly from every possible angle *and* stays flush with that varying curvature — chasing
    // one across many rounds kept fixing whichever marker was being tested while quietly
    // breaking another. Fixing the *left/right* angle turns that into a solvable problem: every
    // charm can simply be oriented to face this one specific, known camera position (see
    // orientToNormal), which guarantees all of them read clearly from the angle a customer
    // actually sees.
    // Up/down tilt is still allowed within a modest range — unlike full orbit, a limited tilt
    // doesn't move far enough off DEFAULT_VIEW_POINT to make any charm unreadable, and it lets a
    // customer glance at the toe from slightly above or below without feeling locked rigid.
    controls.enableRotate = true;
    controls.update();
    const lockedAzimuth = controls.getAzimuthalAngle();
    controls.minAzimuthAngle = lockedAzimuth;
    controls.maxAzimuthAngle = lockedAzimuth;
    const centerPolarAngle = controls.getPolarAngle();
    const VERTICAL_TILT_RANGE = THREE.MathUtils.degToRad(20);
    controls.minPolarAngle = centerPolarAngle - VERTICAL_TILT_RANGE;
    controls.maxPolarAngle = centerPolarAngle + VERTICAL_TILT_RANGE;
    // Panning would slide the camera sideways off this same fixed angle just as surely as
    // left/right rotation would — disabled for the same reason. Zoom is left on: it moves the
    // camera closer or farther along the same viewing direction, which doesn't change which way
    // anything faces.
    controls.enablePan = false;
    controls.update();
  }

  scene.add(new THREE.HemisphereLight(0xffffff, 0x666666, 1.2));
  const key = new THREE.DirectionalLight(0xffffff, 2.2);
  key.position.set(0.6, 1, 0.4);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  // Tight frustum fit to the object — the Three.js default is far too large for a ~0.3m
  // object and starves the shadow map of resolution, causing moire/"shadow acne".
  key.shadow.camera.left = -0.25;
  key.shadow.camera.right = 0.25;
  key.shadow.camera.top = 0.25;
  key.shadow.camera.bottom = -0.25;
  key.shadow.camera.near = 0.1;
  key.shadow.camera.far = 2;
  key.shadow.bias = -0.0015;
  key.shadow.normalBias = 0.002;
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.8);
  fill.position.set(-0.6, 0.4, -0.4);
  scene.add(fill);

  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(1, 64),
    new THREE.ShadowMaterial({ opacity: 0.18 })
  );
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  const loader = new GLTFLoader();
  const tintableMeshesByPart = { clog: [], strap: [] };
  let clogRoot = null;

  function loadPart(url, partKey) {
    return new Promise((resolve, reject) => {
      loader.load(url, (gltf) => {
        const partRoot = gltf.scene;
        partRoot.traverse((obj) => {
          if (!obj.isMesh) return;
          obj.castShadow = true;
          obj.receiveShadow = true;
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((mat) => {
            if (mat && mat.name && mat.name.startsWith(TINTABLE_MATERIAL_PREFIX)) {
              const cloned = mat.clone();
              // Strip the baked-in "gem-cut" normal map from the source model so the surface
              // renders as plain smooth plastic instead of that branded facet texture.
              cloned.normalMap = null;
              cloned.needsUpdate = true;
              if (Array.isArray(obj.material)) {
                obj.material = obj.material.map((m) => (m === mat ? cloned : m));
              } else {
                obj.material = cloned;
              }
              tintableMeshesByPart[partKey].push(cloned);
            }
          });
        });
        scene.add(partRoot);
        if (partKey === 'clog') clogRoot = partRoot;
        resolve(partRoot);
      }, undefined, reject);
    });
  }

  function setPartColor(partKey, hex) {
    // A true #000000 has zero diffuse reflectance under PBR lighting, so it renders as a
    // flat, detail-less silhouette instead of looking like a real black material. Floor each
    // channel slightly for the 3D preview only — the swatch, colour name, and cart payload
    // all still use the real hex value, this only affects how dark the render can go.
    const c = new THREE.Color(hex);
    const floor = 0.05;
    c.r = Math.max(c.r, floor);
    c.g = Math.max(c.g, floor);
    c.b = Math.max(c.b, floor);
    tintableMeshesByPart[partKey].forEach((mat) => mat.color.copy(c));
  }

  // --- Charm billboards: small circular image planes pinned to fixed anchor points on the
  // clog body. One reusable plane per slot, shown/hidden and re-textured as charms are
  // toggled — this is what lets 100+ charm designs work with zero 3D modelling per charm. ---
  const charmSlotMeshes = [];
  const charmTextureCache = new Map(); // productId -> THREE.CanvasTexture

  function buildCharmSlots() {
    const anchors = clogRoot ? findCharmAnchors(clogRoot) : [];
    const positions = anchors.length ? anchors : FALLBACK_CHARM_SLOT_POSITIONS.map((p) => new THREE.Vector3(...p));
    if (!anchors.length) {
      console.warn('[custom-clog-builder] no "charm N" marker nodes found in the clog model — using approximate fallback positions.');
    }
    const slotGeometry = new THREE.CircleGeometry(CHARM_SLOT_SIZE / 2, 24);
    const slotCount = Math.min(maxCharms, positions.length);
    // Computed once and reused for every marker — see computeOutwardNormal for why this is
    // needed (disambiguating which side of a thin double wall a surface normal belongs to).
    const centroid = anchors.length ? computeMeshCentroid(clogRoot) : null;
    for (let i = 0; i < slotCount; i++) {
      const pos = positions[i];
      const mat = new THREE.MeshBasicMaterial({
        transparent: true, depthWrite: false,
        // Front-facing only, not DoubleSide. This model's perforations are real holes through
        // to the shoe's hollow interior, and a double-sided plane sitting just outside one is
        // visible through it from inside the cavity — its back face, showing right through the
        // hole. That's not the charm being embedded in the material (checked directly: from the
        // normal, front-facing viewing angle, full size, it sits perfectly flush), but it reads
        // exactly like it at a glance, and a customer has no reason to ever view the inside of
        // their own shoe anyway. Rendering only the front face removes that view entirely.
        side: THREE.FrontSide,
        opacity: 0, // invisible until a texture actually loads — no placeholder circle if a charm image fails
      });
      const mesh = new THREE.Mesh(slotGeometry, mat);
      mesh.position.copy(pos);
      // The real surface normal here is still used for *position* — standing the plane off the
      // shoe by the right amount and in the right direction to actually clear the surface (see
      // computeOutwardNormal) — even though orientToNormal no longer uses it for facing (see
      // that function for why: with the preview's rotation now locked, every charm faces the one
      // fixed camera position directly instead of its own local surface direction).
      const originalNormal = anchors.length ? computeOutwardNormal(pos, clogRoot, centroid) : null;
      if (originalNormal) {
        // Snap sideways onto the real centre of the nearest perforation hole, if there is one
        // nearby — the authored marker position is sometimes a little off, which otherwise
        // leaves a sliver of the hole visible next to the charm instead of fully covered.
        const hole = findNearestHoleCenter(pos, originalNormal, clogRoot, centroid);
        const holePosition = hole ? hole.position : pos;
        // Standoff direction: toward the fixed camera point, not along originalNormal. That
        // normal is measured off whichever real mesh triangle sits nearest the hole, and at a
        // handful of markers (confirmed directly — one on the toe's side curve rendered visibly
        // sunk, no gap around its hole, next to other markers that stood proud correctly) the
        // nearest triangle turns out to belong to a slightly different facet than the one the
        // camera is actually looking down onto, so offsetting along it doesn't reliably move the
        // charm toward the viewer. Since the viewing angle is fixed (see DEFAULT_VIEW_POINT),
        // "toward the camera" is a direction that's always correct for this one purpose —
        // guaranteeing the charm ends up on the visible side of the surface, standing proud —
        // the same reasoning already applied to orientToNormal's facing direction below.
        const toCameraDir = DEFAULT_VIEW_POINT.clone().sub(holePosition).normalize();
        mesh.position.copy(holePosition).addScaledVector(toCameraDir, CHARM_SLOT_SIZE * 0.28);
        orientToNormal(mesh);
      } else {
        mesh.lookAt(CHARM_FACING_FALLBACK_TARGET);
      }
      mesh.visible = false;
      mesh.renderOrder = 10;
      (clogRoot || scene).add(mesh);
      charmSlotMeshes.push(mesh);
    }
    shrinkCrowdedSlots(charmSlotMeshes);
    return slotCount;
  }

  // Two of this model's charm markers sit only ~32mm apart (measured directly) — closer than
  // the ~42mm a full-size charm disc needs, so at full size they don't just look crowded on
  // screen, their discs genuinely overlap in 3D regardless of viewing angle. Each charm still
  // has to stay centred on its own real hole (that's the whole point of the snapping above), so
  // the only lever here is size: shrink just the slots that are actually too close to a
  // neighbour, just enough to clear it, and leave every other slot at full size.
  // How far that shrink is allowed to go is capped higher than "still reads clearly" alone would
  // suggest — checked directly against one of these two markers with a wordmark-style charm
  // (mostly open space between and inside the letters): at ~73% scale the real hole showed
  // through those internal gaps even though the disc was still correctly centred on it, and only
  // stopped at 85%+. A charm's own artwork can't be relied on to have solid coverage everywhere
  // inside its bounding circle, so the floor has to protect hole coverage, not just legibility.
  const MIN_CENTER_GAP = CHARM_SLOT_SIZE * 0.95;
  const MIN_SCALE = 0.85;
  function shrinkCrowdedSlots(meshes) {
    const scales = new Array(meshes.length).fill(1);
    for (let a = 0; a < meshes.length; a++) {
      for (let b = a + 1; b < meshes.length; b++) {
        const dist = meshes[a].position.distanceTo(meshes[b].position);
        if (dist >= MIN_CENTER_GAP) continue;
        const factor = Math.max(MIN_SCALE, dist / MIN_CENTER_GAP);
        scales[a] = Math.min(scales[a], factor);
        scales[b] = Math.min(scales[b], factor);
      }
    }
    meshes.forEach((mesh, i) => { if (scales[i] < 1) mesh.scale.setScalar(scales[i]); });
  }

  // Product photos often have TWO layers of non-charm backdrop: the plain studio background,
  // and — some of these specifically — a separate light grey/off-white mounting card the charm
  // is physically shot sitting on, plus sometimes a soft grey drop shadow. Classifying by "is
  // this light and essentially colourless" rather than "does this match one exact sampled
  // colour" catches all of that in one pass, since none of it has real charm colour in it —
  // while an enclosed light detail that's actually part of the charm (e.g. a shark's white
  // teeth) survives, since the flood fill can only remove pixels reachable from the image
  // border through other qualifying pixels. This makes the background genuinely transparent —
  // it does NOT invent a backing plate behind the charm. Not every charm has a solid backing
  // disc in real life (some are flat printed shapes with no base at all), so guessing one
  // universally was wrong; whatever the charm's real silhouette is, that's what shows.
  function removeStudioBackground(ctx, width, height) {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const LIGHTNESS_HARD = 232; // fully backing at/above this lightness (with low saturation)
    const DARK_PROTECT = 90; // kept as real charm material at/below this — protects a solid
    // dark charm backing already present in the photo from being treated as shadow.
    // Everything between fades smoothly — wide on purpose, so a product photo's soft grey drop
    // shadow blends into the backing gradually instead of acting as a hard wall the flood fill
    // can't cross, which otherwise traps background pixels behind it as an unreachable island.
    const SATURATION_MAX = 0.16; // above this, treat as real charm colour regardless of lightness

    function keepFactor(i) {
      // A pixel the source image never actually covered (canvas letterboxing from a
      // contain-fit on a non-square photo) is blank (0,0,0,0), not real black — treat it as
      // trivially background, or it reads identically to a genuine black charm base and forms
      // a wall the flood fill can never cross, trapping everything inside it as unreachable.
      if (data[i + 3] === 0) return 0;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      if (saturation > SATURATION_MAX) return 1; // real colour — always kept as-is
      if (max <= DARK_PROTECT) return 1;
      if (max >= LIGHTNESS_HARD) return 0;
      return 1 - (max - DARK_PROTECT) / (LIGHTNESS_HARD - DARK_PROTECT);
    }

    const visited = new Uint8Array(width * height);
    const stack = [];
    for (let x = 0; x < width; x++) { stack.push(x, 0, x, height - 1); }
    for (let y = 0; y < height; y++) { stack.push(0, y, width - 1, y); }

    while (stack.length) {
      const y = stack.pop();
      const x = stack.pop();
      if (x < 0 || y < 0 || x >= width || y >= height) continue;
      const p = y * width + x;
      if (visited[p]) continue;
      visited[p] = 1;
      const i = p * 4;
      const keep = keepFactor(i);
      if (keep >= 1) continue; // hit the real charm — don't cross into it
      data[i + 3] = Math.round(data[i + 3] * keep); // genuinely transparent — no invented backing
      stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
    }
    ctx.putImageData(imageData, 0, 0);
  }

  function circleCropTexture(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const size = 256;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        // Contain-fit (not cover-fit) — a charm's real silhouette needs to be fully visible,
        // not cropped to fill a circle the way the old white-background version was.
        const scale = Math.min(size / img.width, size / img.height) * 0.9;
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        removeStudioBackground(ctx, size, size);
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        resolve(texture);
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  }

  // Expands {charm, qty} entries into one list entry per physical unit, in selection order —
  // e.g. picking 3 of charm A then 2 of charm B flattens to [A, A, A, B, B]. Only the first
  // `charmSlotMeshes.length` units get an actual slot on the shoe (see the loop below); a
  // customer can select more total charms than there are markers to show them on, and the rest
  // just don't appear in the 3D preview while still adding to cart normally.
  function flattenedCharmUnits() {
    const units = [];
    selected.charm.forEach((entry) => {
      for (let i = 0; i < entry.qty; i++) units.push(entry.charm);
    });
    return units;
  }

  // Dispatches to whichever charm rendering path is active — see is3DCharmMode above. Every
  // call site (the qty helpers, the initial model-load handler) calls this one function and
  // doesn't need to know which mode is live.
  function assignCharmSlots() {
    if (is3DCharmMode) { assignCharm3DModels(); return; }
    assignBillboardCharmSlots();
  }

  function assignBillboardCharmSlots() {
    const units = flattenedCharmUnits();
    units.forEach((charm, i) => {
      const mesh = charmSlotMeshes[i];
      if (!mesh) return; // beyond the model's real marker count — still added to cart, just not shown here
      mesh.visible = true;
      if (charmTextureCache.has(charm.productId)) {
        mesh.material.map = charmTextureCache.get(charm.productId);
        mesh.material.opacity = 1;
        mesh.material.needsUpdate = true;
        return;
      }
      if (!charm.image) return;
      circleCropTexture(charm.image).then((texture) => {
        charmTextureCache.set(charm.productId, texture);
        mesh.material.map = texture;
        mesh.material.opacity = 1;
        mesh.material.needsUpdate = true;
      }).catch((err) => {
        console.warn('[custom-clog-builder] charm image failed to load for 3D preview:', charm.name, err);
      });
    });
    // Hide any slots beyond the current selection count.
    for (let i = units.length; i < charmSlotMeshes.length; i++) {
      if (charmSlotMeshes[i]) charmSlotMeshes[i].visible = false;
    }
  }

  // --- Real 3D charm models (see is3DCharmMode above) ---
  // Unlike the old one-marker-each demo set, these are generic standalone meshes (made in bulk,
  // not individually fitted to this clog in Blender) — so instead of one baked-position file per
  // marker kept permanently in the scene, a design is loaded on demand, cloned onto whichever
  // marker "slot" it's been assigned by flattenedCharmUnits() (same slot-assignment rule the
  // billboard system uses — see assignBillboardCharmSlots), and only that clone's own transform
  // needs to change when the selection changes. This is what makes a catalog of 100+ designs
  // financially loadable at all: only the handful actually picked are ever fetched.
  // Small fixed clearance ON TOP OF each design's own measured depth (see loadCharmModel's
  // backZ) — just enough to avoid z-fighting flush against the surface, not the thing doing the
  // real clearing work. Earlier versions made this fixed constant the *entire* offset (0.003,
  // then 0.0008, then a flat 0.0015 meant to also cover tilt error at the worst marker) — that
  // last one fixed the cut edges there but overshot every well-behaved marker along with it,
  // floating charms that never needed the extra (confirmed directly). measureFootprintClearance
  // below now measures what each specific marker actually needs by testing the real surface, so
  // this constant goes back to just covering geometry/z-fighting.
  const CHARM_STANDOFF = 0.0004;
  // A small buffer on top of the exact minimum measureFootprintClearance finds — that minimum is
  // "just barely not clipping", and floating-point/sampling-resolution slack right at that exact
  // boundary isn't worth the risk of a hairline clip reappearing.
  const FOOTPRINT_CLEARANCE_MARGIN = 0.0002;
  // A real charm needs to read as a small flat accessory stuck onto the shoe, not a free-standing
  // figurine — checked directly against the flat billboard's CHARM_SLOT_SIZE (0.05m): at that
  // size, an actual 3D character's own volume and protruding limbs made it look much larger and
  // more "standing up" than a same-diameter flat disc ever does. Sized down independently of the
  // billboard constant instead of reusing it.
  const CHARM_MESH_SIZE = 0.024;
  const WORLD_UP = new THREE.Vector3(0, 1, 0);
  // A real charm needs to hang the same way up regardless of which marker (and therefore which
  // outward-normal direction) it's on — Object3D.quaternion.setFromUnitVectors only guarantees
  // where the mesh's face axis points, not the roll around it, so two markers with different
  // normals can (and did — confirmed directly, one design rendered upside-down at one marker but
  // not another) end up with completely different, arbitrary twist. Building an explicit look-at
  // style basis anchored to world "up" instead removes that degree of freedom: every marker gets
  // the same upright reference, tilted only by how much its own surface normal actually differs
  // from straight-out. Falls back to a different up reference only when the normal is nearly
  // parallel to world up (the cross product below would be degenerate otherwise), matching the
  // same edge case findCharmAnchors' neighbours already have to handle.
  function orientToOutwardNormal(mesh, normal) {
    const upRef = Math.abs(normal.dot(WORLD_UP)) > 0.95 ? new THREE.Vector3(0, 0, 1) : WORLD_UP;
    const right = new THREE.Vector3().crossVectors(upRef, normal).normalize();
    const up = new THREE.Vector3().crossVectors(normal, right).normalize();
    mesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(right, up, normal));
  }
  let charmMarkerFrames = []; // one per real marker, built once the clog model resolves
  const charmSlotObjects = []; // sparse, index-aligned with charmMarkerFrames: {charm, mesh} | undefined
  const charmModelCache = new Map(); // modelUrl -> loaded THREE.Object3D template, load-once/clone-per-slot
  const charmModelPromises = new Map(); // modelUrl -> in-flight load Promise, de-dupes concurrent requests

  // Same "markers closer together than a full-size charm needs" problem the billboard slots
  // solve (see shrinkCrowdedSlots below) applies here too, just against real mesh bounding boxes
  // instead of flat discs — factored out so both paths share one rule instead of two that could
  // quietly drift apart. Takes its own gap threshold (rather than reusing the billboard's
  // MIN_CENTER_GAP directly) since a real 3D charm's footprint is a different size than a
  // billboard disc — see CHARM_MESH_SIZE.
  function computeCrowdingScales(positions, minGap) {
    const scales = new Array(positions.length).fill(1);
    for (let a = 0; a < positions.length; a++) {
      for (let b = a + 1; b < positions.length; b++) {
        const dist = positions[a].distanceTo(positions[b]);
        if (dist >= minGap) continue;
        const factor = Math.max(MIN_SCALE, dist / minGap);
        scales[a] = Math.min(scales[a], factor);
        scales[b] = Math.min(scales[b], factor);
      }
    }
    return scales;
  }
  const MIN_CENTER_GAP_3D = CHARM_MESH_SIZE * 0.95;
  // A first attempt at fixing the single-triangle noise blended the raw reading toward "straight
  // out from the whole shoe's centre" (see git history). That's wrong at any marker that isn't
  // roughly radially placed on a convex blob — checked directly: it pulled at least one marker's
  // normal far enough off that the charm's standoff and thin axis pointed into a nearby
  // perforation instead of across it, so the mesh partly fell through the hole into the shoe's
  // interior instead of standing proud over it. A global "away from the centre" guess has no way
  // to know that; it ignores the real local geometry entirely. Averaging several *real* normal
  // readings sampled a few millimetres around the marker (still real geometry, just several
  // triangles' worth instead of trusting whichever single one happens to be nearest) smooths the
  // same single-triangle noise without that failure mode, because every sample is still an actual
  // measurement of this shoe's actual local surface.
  const NORMAL_SAMPLE_RADIUS = 0.006; // metres — small enough to stay within "this marker's own
  // patch of surface", large enough to move past a single bad triangle right at a perforation's
  // bevelled rim (where these markers tend to sit).
  function computeSmoothedOutwardNormal(pos, meshRoot, centroid, seedNormal) {
    const up = Math.abs(seedNormal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const tangentA = new THREE.Vector3().crossVectors(up, seedNormal).normalize();
    const tangentB = new THREE.Vector3().crossVectors(seedNormal, tangentA).normalize();
    const offsets = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]];
    const sum = new THREE.Vector3();
    let count = 0;
    offsets.forEach(([u, v]) => {
      const samplePos = pos.clone().addScaledVector(tangentA, u * NORMAL_SAMPLE_RADIUS).addScaledVector(tangentB, v * NORMAL_SAMPLE_RADIUS);
      const n = computeOutwardNormal(samplePos, meshRoot, centroid);
      if (n) { sum.add(n); count++; }
    });
    return count ? sum.normalize() : seedNormal;
  }

  // Directly measures how much standoff a charm at (holePosition, normal) actually needs to
  // clear the REAL surface across its whole footprint, not just at its own centre point — a flat
  // charm's plane and the shoe's actual (curved, sometimes locally irregular) surface only agree
  // exactly at one point; how far they diverge at the charm's edges varies a lot by marker (some
  // sit on a gently curved patch, others near a sharper fold or right beside a hole) and isn't
  // something a single angle or a blanket constant reliably predicts — checked directly: an
  // attempt at exactly that (measuring the angle between two different normal readings) came back
  // large and inconsistent for reasons that turned out to have nothing to do with how much the
  // surface actually diverges, since a marker sitting near a hole's edge naturally reads very
  // differently there than out on the open skin regardless of true local flatness. Casting real
  // rays at sample points across the charm's actual footprint and reading how far the true
  // surface sits from each one is a direct measurement instead of an inference — it can't be
  // fooled the same way, whatever the underlying reason for a given marker being trickier.
  function measureFootprintClearance(holePosition, normal) {
    const upRef = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1);
    const right = new THREE.Vector3().crossVectors(upRef, normal).normalize();
    const up = new THREE.Vector3().crossVectors(normal, right).normalize();
    const halfWidth = CHARM_MESH_SIZE / 2;
    const meshes = collectSurfaceMeshes(clogRoot);
    const PROBE_OUT = 0.02; // metres — starts well clear of any real charm's own thickness
    // Centre, the 4 edge midpoints, and the 4 corners of the charm's own square-ish footprint —
    // enough coverage to catch a corner or edge dipping into the surface even when the centre
    // itself reads perfectly flush.
    const offsets = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1], [0.7, 0.7], [-0.7, 0.7], [0.7, -0.7], [-0.7, -0.7]];
    let maxNeeded = 0;
    offsets.forEach(([u, v]) => {
      const flatPoint = holePosition.clone().addScaledVector(right, u * halfWidth).addScaledVector(up, v * halfWidth);
      const rayOrigin = flatPoint.clone().addScaledVector(normal, PROBE_OUT);
      const raycaster = new THREE.Raycaster(rayOrigin, normal.clone().negate(), 0, PROBE_OUT * 2);
      const hits = raycaster.intersectObjects(meshes, false);
      if (hits.length) {
        const neededHere = PROBE_OUT - hits[0].distance; // how far this sample point sits behind (+) the real surface
        if (neededHere > maxNeeded) maxNeeded = neededHere;
      }
    });
    return maxNeeded;
  }

  // Computes each marker's real position/outward-normal/hole-snapped position exactly the way
  // buildCharmSlots does for the billboard planes, but without creating any billboard geometry —
  // this mode places real meshes instead, on demand (see assignCharm3DModels below).
  function buildCharmMarkerFrames() {
    const anchors = clogRoot ? findCharmAnchors(clogRoot) : [];
    const positions = anchors.length ? anchors : FALLBACK_CHARM_SLOT_POSITIONS.map((p) => new THREE.Vector3(...p));
    if (!anchors.length) {
      console.warn('[custom-clog-builder] no "charm N" marker nodes found in the clog model — using approximate fallback positions.');
    }
    const centroid = anchors.length ? computeMeshCentroid(clogRoot) : null;
    const slotCount = Math.min(maxCharms, positions.length);
    const frames = [];
    for (let i = 0; i < slotCount; i++) {
      const pos = positions[i];
      const rawNormal = anchors.length ? computeOutwardNormal(pos, clogRoot, centroid) : null;
      if (rawNormal) {
        const hole = findNearestHoleCenter(pos, rawNormal, clogRoot, centroid);
        // findNearestHoleCenter already re-measures the normal genuinely on the skin surrounding
        // the snapped hole (marching outward from the void's edge in several directions and
        // averaging — see its own comments) specifically because orienting with the pre-snap
        // marker normal instead "makes it look tilted / half-sunk relative to the surface it's
        // actually resting on". That's exactly this bug, already solved once for the billboard
        // path; reusing hole.normal here (rather than re-deriving a separate, weaker average
        // myself, which can sample straight into the hole's own void for an irregular/elongated
        // perforation) is what actually fixes it. Only falls back to a plain local average when
        // no hole was found nearby to re-measure against.
        const holePosition = hole ? hole.position : pos;
        const normal = (hole && hole.normal) ? hole.normal : computeSmoothedOutwardNormal(holePosition, clogRoot, centroid, rawNormal);
        const measuredClearance = measureFootprintClearance(holePosition, normal);
        frames.push({ position: pos, normal, holePosition, measuredClearance });
      } else {
        frames.push({ position: pos, normal: CHARM_FACING_FALLBACK_TARGET.clone().sub(pos).normalize(), holePosition: pos, measuredClearance: 0 });
      }
    }
    const scales = computeCrowdingScales(frames.map((f) => f.holePosition), MIN_CENTER_GAP_3D);
    frames.forEach((f, i) => { f.scaleFactor = scales[i]; });
    return frames;
  }

  function loadCharmModel(url) {
    if (charmModelCache.has(url)) return Promise.resolve(charmModelCache.get(url));
    if (charmModelPromises.has(url)) return charmModelPromises.get(url);
    const promise = new Promise((resolve, reject) => {
      new GLTFLoader().load(url, (gltf) => {
        gltf.scene.traverse((obj) => {
          if (obj.isMesh) { obj.castShadow = true; obj.receiveShadow = true; }
        });
        // A fixed standoff distance can't work across designs — a flat character charm and a
        // chunky, rounded one (checked directly: a cupcake design's own depth measured over 3x
        // deeper than a flat character's) need completely different clearance to both avoid
        // floating and avoid burying the deep one halfway into the shoe. Measuring each design's
        // own real depth right after it loads (in its own local space, before any scale/position
        // is applied — see placeCharmMesh) and offsetting from ITS actual back face fixes both at
        // once, for every design, with no per-file tuning.
        const box = new THREE.Box3().setFromObject(gltf.scene);
        gltf.scene.userData.backZ = box.min.z;
        // CHARM_MESH_SIZE assumed every export's local Y axis is the one Meshy normalized to
        // exactly 1.0 — true for the first 5 tall, character-shaped samples, but a wide/short
        // design (a butterfly with its wingspan running along X, not Y) can come back with X
        // bigger than 1.0 instead, since Meshy normalizes whichever axis the design's actual
        // longest dimension falls on, not Y specifically. Scaling that design by a factor
        // calibrated for "Y is 1.0" makes its true footprint bigger than every other charm at the
        // same nominal size — big enough, on a crowded marker, to reach into a neighbouring hole
        // or past the shoe's own edge. Measuring each design's own real footprint (X/Y only — Z
        // is the thin depth axis, unrelated to how wide it reads face-on) and scaling from that
        // instead of a blind constant fixes it for any aspect ratio, not just tall ones.
        const size = box.getSize(new THREE.Vector3());
        gltf.scene.userData.footprintSize = Math.max(size.x, size.y) || 1;
        resolve(gltf.scene);
      }, undefined, reject);
    }).then((template) => {
      charmModelCache.set(url, template);
      return template;
    }).catch((err) => {
      charmModelPromises.delete(url); // failed load shouldn't permanently block a retry
      console.error('[custom-clog-builder] failed to load 3D charm model:', url, err);
      throw err;
    });
    charmModelPromises.set(url, promise);
    return promise;
  }

  function placeCharmMesh(mesh, frame) {
    // Scaled so THIS design's own measured footprint (see loadCharmModel) ends up at
    // CHARM_MESH_SIZE, not by applying that constant directly to a design whose local units
    // might not already be normalized the same way as the others.
    const footprintSize = mesh.userData.footprintSize || 1;
    const scale = (CHARM_MESH_SIZE / footprintSize) * frame.scaleFactor;
    mesh.scale.setScalar(scale);
    // backZ (from loadCharmModel) is this specific design's own real back face in its local,
    // unscaled space — almost always negative (the mesh's depth straddles its own origin at
    // roughly this design's centre). Scaling it and subtracting pushes the origin out far enough
    // that the design's actual back face — not some generic assumed one — lands right at the
    // clearance distance off the surface, however deep or shallow this particular design is.
    const backZ = mesh.userData.backZ || 0;
    // measuredClearance (see buildCharmMarkerFrames) is however much THIS specific marker's real
    // surface actually requires, measured directly by raycasting — a well-behaved marker reads
    // ~0 here and gets only the baseline standoff, while a genuinely tricky one gets exactly what
    // its own real geometry calls for, not a guess.
    const extra = (frame.measuredClearance || 0) + FOOTPRINT_CLEARANCE_MARGIN;
    const pushOut = (CHARM_STANDOFF + extra) * frame.scaleFactor - backZ * scale;
    mesh.position.copy(frame.holePosition).addScaledVector(frame.normal, pushOut);
    orientToOutwardNormal(mesh, frame.normal);
  }

  function assignCharm3DModels() {
    const units = flattenedCharmUnits();
    charmMarkerFrames.forEach((frame, i) => {
      const unit = units[i];
      const current = charmSlotObjects[i];
      if (!unit) {
        if (current) current.mesh.visible = false;
        return;
      }
      if (current && current.charm === unit) {
        current.mesh.visible = true;
        return;
      }
      if (current) current.mesh.visible = false; // hide the outgoing design while the new one loads
      loadCharmModel(unit.modelUrl).then((template) => {
        // Selection may have changed again before this resolved (fast re-toggling, or the clear
        // button) — only place it if this slot still actually wants this design.
        if (flattenedCharmUnits()[i] !== unit) return;
        const mesh = template.clone(true);
        placeCharmMesh(mesh, frame);
        clogRoot.add(mesh);
        charmSlotObjects[i] = { charm: unit, mesh };
      }).catch(() => {}); // already logged in loadCharmModel; nothing more to do per-slot
    });
  }

  // Tabs, swatches/grid, and cart total all work independently of whether the 3D preview
  // manages to load — a broken/missing model file should never take down the rest of the UI.
  renderTabs();
  renderStep();
  renderTotal();

  // On mobile this page is a single fixed screen — theme header on top, the preview, then the
  // drawer and bottom bars — with no page scrolling at all (the footer simply isn't reachable
  // here), which is why there is nothing to scroll into place any more. Two things make that work:
  //  1. `ccb-no-scroll` on <html> (see the CSS) freezes the page. It's lifted again on the order
  //     summary, which is a long list that does need to scroll.
  //  2. The preview's height is whatever is left between the theme header and the drawer, so the
  //     distance from the top of the page to the preview (`--ccb-top`) is measured and published
  //     for the CSS. The header, banners and fonts finish loading after first paint and change
  //     that distance, so it's re-measured as they do.
  function syncPreviewTop() {
    if (!isMobileLayout()) return;
    const top = previewEl.getBoundingClientRect().top + window.scrollY;
    const current = parseFloat(root.style.getPropertyValue('--ccb-top'));
    if (Number.isNaN(current) || Math.abs(current - top) > 0.5) root.style.setProperty('--ccb-top', `${top}px`);
  }
  function applyScrollLock() {
    const lock = isMobileLayout() && !root.classList.contains('is-summary');
    document.documentElement.classList.toggle('ccb-no-scroll', lock);
    if (lock) {
      window.scrollTo(0, 0);
      syncPreviewTop();
    }
  }
  if (isMobileLayout() && 'scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  // The step drawer starts expanded on mobile (the customer can still dismiss it with the ×, and
  // any tab tap brings it back).
  expandMobileSheet();
  applyScrollLock();
  window.addEventListener('load', () => { applyScrollLock(); });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncPreviewTop);
  [300, 800, 1600, 3000].forEach((ms) => setTimeout(syncPreviewTop, ms));
  window.addEventListener('resize', () => { applyScrollLock(); syncPreviewTop(); });
  window.addEventListener('orientationchange', () => setTimeout(() => { applyScrollLock(); syncPreviewTop(); }, 200));
  // Anything that grows or shrinks the page above the builder (announcement bar, header,
  // promo strip finishing their own loading) moves the preview — follow it.
  const pageWrapperEl = document.querySelector('.body-content-wrapper') || document.body;
  new ResizeObserver(syncPreviewTop).observe(pageWrapperEl);

  Promise.all([
    loadPart(config.clogModelUrl, 'clog'),
    loadPart(config.strapModelUrl, 'strap'),
  ]).then(() => {
    statusEl.textContent = '';
    steps.forEach((step) => {
      if (step.kind === 'single') setPartColor(step.key, selected[step.key].hex);
    });
    if (is3DCharmMode) {
      charmMarkerFrames = buildCharmMarkerFrames();
      previewSlotCount = charmMarkerFrames.length;
      assignCharmSlots(); // picks up any charms selected before the clog model finished loading
      if (previewSlotCount !== maxCharms) renderStep();
    } else {
      previewSlotCount = buildCharmSlots();
      if (previewSlotCount !== maxCharms) {
        // Re-render so the "(first N shown in the 3D preview)" note (see renderStep) reflects
        // the model's real marker count instead of the placeholder it started with, and so any
        // charms already picked before the model finished loading get assigned into the
        // now-known-correct slot count.
        assignCharmSlots();
        renderStep();
      }
    }
  }).catch((err) => {
    statusEl.textContent = 'Could not load 3D preview — check that the model URLs are correct.';
    console.error('[custom-clog-builder]', err);
  });

  // The browser Fullscreen API is not available for ordinary elements on iPhone (Safari, and
  // Chrome/Firefox there too since they all use Safari's engine), so `requestFullscreen` simply
  // doesn't exist and the button did nothing. Where the native API is missing — or refuses —
  // this falls back to pinning the preview over the whole screen with CSS (`is-pseudo-fullscreen`),
  // which looks the same to the customer and works everywhere. Native stays the first choice
  // wherever it exists.
  const nativeFsRequest = previewEl.requestFullscreen || previewEl.webkitRequestFullscreen;
  const nativeFsExit = document.exitFullscreen || document.webkitExitFullscreen;
  const fullscreenLabel = fullscreenBtn.textContent;
  let pseudoFullscreen = false;
  const nativeFsElement = () => document.fullscreenElement || document.webkitFullscreenElement;
  function syncFullscreenLabel() {
    fullscreenBtn.textContent = pseudoFullscreen || nativeFsElement() ? '✕ Exit fullscreen' : fullscreenLabel;
  }
  function setPseudoFullscreen(on) {
    pseudoFullscreen = on;
    previewEl.classList.toggle('is-pseudo-fullscreen', on);
    document.documentElement.classList.toggle('ccb-fullscreen-lock', on);
    syncFullscreenLabel();
  }
  fullscreenBtn.addEventListener('click', () => {
    if (pseudoFullscreen) { setPseudoFullscreen(false); return; }
    if (nativeFsElement()) { nativeFsExit.call(document); return; }
    if (!nativeFsRequest) { setPseudoFullscreen(true); return; }
    try {
      const result = nativeFsRequest.call(previewEl);
      if (result && typeof result.catch === 'function') result.catch(() => setPseudoFullscreen(true));
    } catch (err) {
      setPseudoFullscreen(true);
    }
  });
  ['fullscreenchange', 'webkitfullscreenchange'].forEach((type) => document.addEventListener(type, syncFullscreenLabel));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && pseudoFullscreen) setPseudoFullscreen(false);
  });

  // `renderer.setSize` throws away and re-creates the whole drawing buffer (multisampled, at the
  // device pixel ratio), which is by far the most expensive thing this page does outside of the
  // render itself. It must therefore run once per genuine size change — never per animation
  // frame — which is why the preview's height no longer animates in the CSS (an animated height
  // fired this observer ~18 times per step change and the model visibly stuttered), and why a
  // callback that reports the size we already rendered at is ignored.
  // Starts at 0 on purpose, not at the viewer's current size: the renderer was created earlier, at
  // whatever size the preview had *then* (before the mobile drawer was expanded by default), so
  // the observer's first callback must always apply the real size instead of being skipped as
  // "unchanged" — doing that left the canvas stuck at its pre-drawer height on first load.
  let renderedW = 0;
  let renderedH = 0;
  new ResizeObserver(() => {
    const w = viewer.clientWidth;
    const h = viewer.clientHeight;
    if (!w || !h || (w === renderedW && h === renderedH)) return;
    renderedW = w;
    renderedH = h;
    camera.aspect = w / h;
    camera.fov = fovForAspect(camera.aspect);
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }).observe(viewer);

  (function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  })();

  // --- Order summary: a last review screen before the real cart submission, swapped in for the
  // customizer entirely (not alongside it) rather than yet another step/tab — reached once the
  // customer clicks through from the last real step (Charms). Nothing about the underlying
  // `selected` state changes here; "Back to Customizer" is just the same swap in reverse. ---
  // On mobile the preview is otherwise a full screen tall, which would push the review list
  // below the fold — `is-summary` shrinks it so the list is right there.
  function showSummary() {
    closeMobileSheet();
    root.classList.add('is-summary');
    customizerViewEl.classList.add('ccb-hidden');
    summaryViewEl.classList.remove('ccb-hidden');
    renderSummary();
    // The review list is taller than the screen, so the page must be free to scroll here.
    applyScrollLock();
    window.scrollTo(0, 0);
  }
  function hideSummary() {
    root.classList.remove('is-summary');
    summaryViewEl.classList.add('ccb-hidden');
    customizerViewEl.classList.remove('ccb-hidden');
    // Back on the customizer the drawer is expanded again, same as on first load.
    expandMobileSheet();
    clearFinalAddError();
    applyScrollLock();
  }
  backToCustomizerBtn.addEventListener('click', hideSummary);

  function renderSummary() {
    summaryListEl.innerHTML = '';

    function addSummaryRow({ hex, image, title, lines, price }) {
      const row = document.createElement('div');
      row.className = 'ccb-summary-row';

      const swatch = document.createElement('div');
      swatch.className = 'ccb-summary-swatch';
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = title;
        swatch.appendChild(img);
      } else if (hex) {
        swatch.style.background = hex;
      }
      row.appendChild(swatch);

      const info = document.createElement('div');
      info.className = 'ccb-summary-info';
      const titleEl = document.createElement('div');
      titleEl.className = 'ccb-summary-title';
      titleEl.textContent = title;
      info.appendChild(titleEl);
      lines.forEach((line) => {
        const lineEl = document.createElement('div');
        lineEl.className = 'ccb-summary-line';
        lineEl.textContent = line;
        info.appendChild(lineEl);
      });
      row.appendChild(info);

      const priceEl = document.createElement('div');
      priceEl.className = 'ccb-summary-price';
      priceEl.textContent = money(price);
      row.appendChild(priceEl);

      summaryListEl.appendChild(row);
    }

    steps.forEach((step) => {
      if (step.kind !== 'single') return; // clog + strap — one included colour each
      const c = selected[step.key];
      if (!c) return;
      const rv = step.key === 'clog' ? resolveClogVariant() : resolveStrapVariant();
      addSummaryRow({ hex: c.hex, image: c.image, title: step.tabLabel, lines: [`Colour: ${c.name}`], price: rv ? rv.price : c.price });
    });

    selected.spareStraps.forEach((entry) => {
      addSummaryRow({
        hex: entry.color.hex,
        image: entry.color.image,
        title: 'Spare Straps',
        lines: [`Colour: ${entry.color.name}`, `${entry.qty} ${entry.qty === 1 ? 'pair' : 'pairs'}`],
        price: spareEntryPrice(entry),
      });
    });

    selected.charm.forEach((entry) => {
      addSummaryRow({
        image: entry.charm.image,
        title: 'Birdees Charms',
        lines: [
          `Category: ${entry.charm.category || 'Charms'}`,
          `Product Name: ${entry.charm.name}${entry.qty > 1 ? ` ×${entry.qty}` : ''}`,
        ],
        price: Number(entry.charm.price) * entry.qty,
      });
    });

    summaryTotalEl.textContent = `Total: ${money(currentTotal())}`;
  }

  // Builds the same multi-line-item payload the old direct-submit flow used — split out so both
  // the summary's "Add to Cart" button (the only thing that actually calls it now) and any future
  // caller can reuse it without duplicating this logic.
  function buildCartItems() {
    const bundleId = 'clog-build-' + Date.now();
    const items = [];
    steps.forEach((step) => {
      if (step.kind === 'size') return; // folded into the clog line item's _size property below, not its own product
      if (step.kind === 'multi') {
        selected[step.key].forEach((entry) => {
          items.push({
            id: Number(entry.charm.variantId),
            quantity: entry.qty,
            properties: { _bundle_id: bundleId, _bundle_part: 'Charm', _bundle_color: entry.charm.name },
          });
        });
      } else if (step.key === 'clog') {
        const c = selected[step.key];
        const rv = resolveClogVariant();
        const properties = { _bundle_id: bundleId, _bundle_part: step.tabLabel, _bundle_color: c.name };
        if (selected.size) properties._size = formatSize(selected.size);
        items.push({ id: Number(rv.variantId), quantity: 1, properties });
      } else if (step.key === 'strap') {
        const c = selected[step.key];
        const rv = resolveStrapVariant();
        const properties = { _bundle_id: bundleId, _bundle_part: step.tabLabel, _bundle_color: c.name };
        if (selected.size) properties._size = formatSize(selected.size);
        items.push({ id: Number(rv.variantId), quantity: 1, properties });
      } else {
        const c = selected[step.key];
        items.push({
          id: Number(c.variantId),
          quantity: 1,
          properties: { _bundle_id: bundleId, _bundle_part: step.tabLabel, _bundle_color: c.name },
        });
      }
    });
    // Spare straps aren't a step, so the loop above never sees them — same size-matched variant as
    // the included strap colour above (a spare pair is meant to match the clog size too), one unit
    // per pair (the strap product is already priced and stocked per pair), so this draws from the
    // exact same per-size inventory instead of a separate product's stock or the colour's flat
    // default variant.
    pruneSpareStraps();
    selected.spareStraps.forEach((entry) => {
      const match = selected.size && Array.isArray(entry.color.variants)
        ? entry.color.variants.find((v) => String(v.size).trim() === selected.size.eu && v.available)
        : null;
      const variantId = match ? match.variantId : entry.color.variantId;
      items.push({
        id: Number(variantId),
        quantity: entry.qty,
        properties: { _bundle_id: bundleId, _bundle_part: 'Spare Strap Pair', _bundle_color: entry.color.name },
      });
    });
    return items;
  }

  // Clicking through from the last real step now opens the review screen instead of submitting
  // straight to cart — the actual submission only happens from the summary's own "Add to Cart".
  addBtn.addEventListener('click', () => {
    if (hasSizeStep && !selected.size) {
      // The button reads "Select a Size" but stays enabled (see renderStep) — this is what
      // actually blocks advancing. On mobile the size grid is inside the sheet, so tapping this
      // while it's closed (e.g. the customer dismissed it without picking anything) should reopen
      // it instead of silently doing nothing.
      openMobileSheet();
      return;
    }
    if (activeStepIndex < steps.length - 1) {
      doneSteps.add(steps[activeStepIndex].key);
      activeStepIndex++;
      renderTabs();
      renderStep();
      return;
    }
    showSummary();
  });

  // This theme (Ella, by Halothemes) has no exported JS API for its own cart sidebar — the real
  // logic (`halo.updateSidebarCart`) lives in a private closure inside theme.js and is never
  // assigned to `window`, and the Dawn-style `cart-notification` element this theme still ships
  // as a dead asset is never actually registered/loaded on any page. Rather than reimplement any
  // of the theme's own logic, this replicates the exact same request sequence a native "Add to
  // cart" button already triggers on this store (confirmed directly against theme.js's
  // `actionAddToCart2` → `Shopify.getCart` → `updateSidebarCart` chain): refetch `/cart.js`, flip
  // the same body class that reveals `#halo-cart-sidebar`, then swap in the same server-rendered
  // partial the theme's own sidebar uses — so the drawer that opens is the real one, not a
  // reimplementation of it.
  // Looks up the sidebar's content target, retrying once after a short delay — on a page where
  // the customer has never opened the cart before, this theme's sidebar markup can still be
  // lazily initializing the first time this runs, so an immediate query can miss it even though
  // it exists a moment later.
  async function findDrawerTarget() {
    const query = () => document.querySelector('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper');
    const first = query();
    if (first) return first;
    await new Promise((resolve) => setTimeout(resolve, 200));
    return query();
  }

  // The drawer reveal is best-effort and can genuinely take a moment (three separate fetches) —
  // deliberately NOT awaited before showing the green "Added" state below, so a slow/lazy sidebar
  // never delays or swallows the one piece of feedback that matters most: confirming the order
  // actually went through. Errors here are caught and logged only; the item is already in the
  // cart by the time this runs.
  async function revealCartDrawer() {
    // no-store on both: a cached copy of either would show the previous cart (missing the bundle
    // that was just added), and a non-ok response must throw rather than inject an error page.
    const getOk = (url) => fetch(url, { cache: 'no-store' }).then((r) => {
      if (!r.ok) throw new Error('Cart refresh failed (' + r.status + ')');
      return r;
    });
    const [cart, html, drawerTarget] = await Promise.all([
      getOk('/cart.js').then((r) => r.json()),
      getOk('/cart?view=ajax_side_cart').then((r) => r.text()),
      findDrawerTarget(),
    ]);
    // Without the sidebar there is nothing to slide open — better to say so than silently do nothing.
    if (!drawerTarget) throw new Error('Cart drawer not found');
    // Content goes in *before* the body class that triggers the theme's own slide-in transition —
    // flipping that class first (the previous order) could start the slide against a still-empty/
    // stale panel, which is what made the drawer occasionally look like it "didn't pull out" even
    // though the item had already been added.
    drawerTarget.innerHTML = html;
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
    requestAnimationFrame(() => document.body.classList.add('cart-sidebar-show'));
  }

  // `routes.cart_add_url` is the plain form-post endpoint (`/cart/add`); the documented AJAX/JSON
  // endpoint is its `.js` variant, which is what this request is — so make sure it's that one
  // regardless of how the section happened to hand the URL over.
  const cartAddAjaxUrl = /\.js(\?|$)/.test(config.cartAddUrl) ? config.cartAddUrl : config.cartAddUrl + '.js';
  const ADD_TIMEOUT_MS = 20000;

  // A failure used to flip the button straight back to "Add to Cart" with the message hidden in
  // the same tick, so a rejected add (e.g. a size/charm that just sold out) looked like the click
  // did nothing. The reason now stays visible under the buttons until the next attempt.
  let finalAddErrorEl = null;
  function clearFinalAddError() {
    if (finalAddErrorEl) finalAddErrorEl.remove();
    finalAddErrorEl = null;
  }
  function showFinalAddError(message) {
    clearFinalAddError();
    finalAddErrorEl = document.createElement('p');
    finalAddErrorEl.setAttribute('role', 'alert');
    finalAddErrorEl.style.cssText = 'margin:10px 0 0;color:#c0392b;font-size:13px;line-height:1.4;text-align:center;';
    finalAddErrorEl.textContent = message;
    finalAddBtn.parentElement.insertAdjacentElement('afterend', finalAddErrorEl);
  }
  function resetFinalAddButton() {
    finalAddBtn.disabled = false;
    finalAddBtn.classList.remove('is-added');
    finalAddLabelEl.classList.remove('ccb-hidden');
    finalAddStatusEl.classList.add('ccb-hidden');
  }

  finalAddBtn.addEventListener('click', async () => {
    if (finalAddBtn.disabled) return;
    finalAddBtn.disabled = true;
    clearFinalAddError();
    finalAddBtn.classList.remove('is-added');
    finalAddLabelEl.classList.add('ccb-hidden');
    finalAddStatusEl.classList.remove('ccb-hidden');
    finalAddStatusEl.textContent = 'Adding…';
    const startedAt = Date.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), ADD_TIMEOUT_MS);
    try {
      const items = buildCartItems();
      const res = await fetch(cartAddAjaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
        signal: controller.signal,
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const reason = payload && (payload.description || payload.message);
        const error = new Error(reason || 'Add to cart failed');
        error.userMessage = res.status === 422
          ? 'Sorry, part of this build is no longer available' + (reason ? ' (' + reason + ')' : '') + '. Please go back and adjust it.'
          : null;
        throw error;
      }

      // Shopify has already accepted the item at this point — show the confirmation right away
      // rather than waiting on the drawer's own fetches below to finish first (see revealCartDrawer).
      finalAddBtn.classList.add('is-added');
      finalAddStatusEl.textContent = 'Added ✓';

      // A hiccup revealing the drawer is a display glitch, not a failed order — but the customer
      // must never be left with a button that says "Added" and nothing else on screen, so if the
      // drawer can't be shown they're taken to the cart page instead.
      const drawerOk = await revealCartDrawer().then(() => true).catch((drawerErr) => {
        console.error('[custom-clog-builder] cart drawer reveal failed (item was still added)', drawerErr);
        return false;
      });
      if (!drawerOk) {
        finalAddStatusEl.textContent = 'Added ✓ Opening cart…';
        setTimeout(() => { window.location.href = '/cart'; }, 700);
        return;
      }

      // Keep the confirmation up for a beat (counted from the click, not from when the drawer
      // finished, so a slow drawer doesn't stretch it), then go back to the customizer — still fully
      // populated — without the smooth-scroll that used to fight the drawer's slide-in.
      const remaining = Math.max(0, 2200 - (Date.now() - startedAt));
      setTimeout(() => {
        hideSummary();
        resetFinalAddButton();
      }, remaining);
    } catch (err) {
      console.error('[custom-clog-builder] add to cart failed', err);
      showFinalAddError(
        err.name === 'AbortError'
          ? 'This is taking too long — please check your connection and try again.'
          : err.userMessage || "We couldn't add this to your cart. Please try again."
      );
      resetFinalAddButton();
    } finally {
      clearTimeout(timeoutId);
    }
  });
}

document.querySelectorAll('[data-custom-clog-builder]').forEach(initClogBuilder);
